/**
 * Employee directory — list + click-to-edit + direct-edit form.
 *
 * Owner and HR both write directly via POST /hr/employees (no
 * pending_change indirection — that table is only used by HR for
 * salary/sensitive edits which live in a different surface).
 */
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { OwnerOnboardContent } from '../owner/Onboard';
import { resolveFileUrl, uploadFile, apiDownload } from '../../lib/api-client';
import { PERSONNEL_DOC_SLOTS, REQUIRED_EMPLOYEE_DOC_TYPES, EMPLOYEE_DOC_TYPE_LABELS, type EmployeeDocType, type WorkMode } from '@payroll/shared';
import { LIFF_ID } from '../../lib/liff';
import {
  useEmployees,
  useUpsertEmployee,
  useNudgeProfile,
  useEmployeeLineHistory,
  useRevealNationalId,
  useRevealNationalIdImage,
  useEmployeeEducation,
  useUpsertEmployeeEducation,
  useDeleteEmployeeEducation,
  useEmployeeWorkHistory,
  useUpsertEmployeeWorkHistory,
  useDeleteEmployeeWorkHistory,
  useEmployeeDocuments,
  useCreateEmployeeDocument,
  useDeleteEmployeeDocument,
  useAllowanceEligibility,
  useAllowanceMaster,
  useUpsertAllowanceEligibility,
  useDeleteAllowanceEligibility,
  useDeductionEligibility,
  useDeductionMaster,
  useUpsertDeductionEligibility,
  useDeleteDeductionEligibility,
  useShiftAssignments,
  useShiftMaster,
  useRotationPatterns,
  useUpsertShiftAssignment,
  useDeleteShiftAssignment,
  useReissuePairing,
  useRevokeLine,
  useTestNotify,
  useSetEmployeeRole,
  useDeleteEmployee,
  useSetEmployeeSalary,
  useEmployeeSalary,
  useDeleteEmployeeSalary,
  useMyCapabilities,
  useBulkSalaryPrep,
  useBulkSetSalary,
  type BulkSalaryEmp,
  type SalaryReference,
  useOrgDepartments,
  useOrgPositions,
  useBranches,
  useSupervisorCandidates,
  useBulkSetSupervisor,
  type AllowanceEligibilityRow,
  type DeductionEligibilityRow,
  type ShiftAssignmentRow,
  useQuotas,
  useUpsertQuota,
} from '../../hooks';
import { Modal } from '../../components/ui/Modal';
import { EducationSection, WorkHistorySection, DocumentsSection } from '../../components/domain/ProfileExtras';
import { UserPlus, X, Pencil, Search, History, Image as ImageIcon, BadgeCheck, Wallet, CalendarClock, Link2, Unlink, Trash2, Copy, Check, Eye, Coins, Download, Sparkles, AlertTriangle, UserCheck, Bell } from 'lucide-react';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { useAuthStore } from '../../lib/store';
import { todayISO } from '../../lib/thai-date';

const WD_ABBR: Record<number, string> = { 1: 'จ', 2: 'อ', 3: 'พ', 4: 'พฤ', 5: 'ศ', 6: 'ส', 7: 'อา' };

type EmpStatus = 'pending_start' | 'active' | 'probation' | 'resigned' | 'terminated';
const STATUS_LABEL: Record<EmpStatus, string> = {
  pending_start: 'รอยืนยันตัวตน',
  active: 'ปกติ',
  probation: 'ทดลองงาน',
  resigned: 'ลาออก',
  terminated: 'เลิกจ้าง',
};
const STATUS_BADGE: Record<EmpStatus, string> = {
  pending_start: 'badge-warn',
  active: 'badge-success',
  probation: 'badge-warn',
  resigned: 'badge-danger',
  terminated: 'badge-danger',
};

interface EmployeeRow {
  emp_code: string;
  first_name: string;
  last_name: string;
  nickname?: string | null;
  email: string;
  phone?: string | null;
  department: string;
  position: string;
  branch_id?: string | null;
  supervisor_email?: string | null;
  supervisor_emp_code?: string | null;
  assigned_role?: string | null;
  start_date: string;
  end_date?: string | null;
  status: 'active' | 'probation' | 'resigned' | 'terminated';
  schedule_mode?: 'company_weekend' | 'roster' | null;
  work_mode?: WorkMode | null;
  attendance_exempt?: boolean | null;
  sso_eligible?: boolean | null;
  tax_withhold?: boolean | null;
  has_commission?: boolean | null;
  rotation_pattern_id?: string | null;
  rotation_anchor_date?: string | null;
  national_id?: string | null;
  national_id_masked?: boolean;
  birth_date?: string | null;
  sso_number?: string | null;
  bank?: string | null;
  bank_account_masked?: string | null;
  id_copy_path?: string | null;
  bankbook_path?: string | null;
  address_current?: string | null;
  address_registered?: string | null;
  emergency_name?: string | null;
  emergency_relationship?: string | null;
  emergency_phone?: string | null;
  profile_completion_pct?: number | null;
  // ทะเบียนพนักงานฉบับเต็ม (mig 20260819000000) — ช่องที่รายงาน 42 คอลัมน์ใช้
  title_th?: string | null;
  title_en?: string | null;
  first_name_en?: string | null;
  last_name_en?: string | null;
  nickname_en?: string | null;
  gender?: 'male' | 'female' | 'other' | null;
  nationality?: string | null;
  division?: string | null;
  section?: string | null;
  employee_group?: string | null;
  position_level?: number | null;
  fingerprint_id?: string | null;
  appointed_date?: string | null;
  contract_end_date?: string | null;
  alien_id?: string | null;
  passport_no?: string | null;
  work_permit_no?: string | null;
  blacklisted?: boolean | null;
  blacklist_note?: string | null;
  weekly_wage?: number | null;
}

type FormData = Omit<EmployeeRow, 'profile_completion_pct'> & {
  emp_code: string;
  /** Full account number — encrypted server-side; blank = leave unchanged. */
  bank_account_full?: string | null;
};

const EMPTY: FormData = {
  emp_code: '',
  first_name: '',
  last_name: '',
  nickname: '',
  email: '',
  phone: '',
  department: '',
  position: '',
  branch_id: '',
  supervisor_email: '',
  supervisor_emp_code: '',
  start_date: '',
  end_date: '',
  status: 'active',
  schedule_mode: 'company_weekend',
  work_mode: 'office',
  attendance_exempt: false,
  sso_eligible: true,
  tax_withhold: true,
  has_commission: false,
  rotation_pattern_id: '',
  rotation_anchor_date: '',
  national_id: '',
  birth_date: '',
  sso_number: '',
  bank: '',
  bank_account_masked: '',
  bank_account_full: '',
  id_copy_path: '',
  bankbook_path: '',
  address_current: '',
  address_registered: '',
  emergency_name: '',
  emergency_relationship: '',
  emergency_phone: '',
  title_th: '',
  title_en: '',
  first_name_en: '',
  last_name_en: '',
  nickname_en: '',
  gender: null,
  nationality: '',
  division: '',
  section: '',
  employee_group: '',
  position_level: null,
  fingerprint_id: '',
  appointed_date: '',
  contract_end_date: '',
  alien_id: '',
  passport_no: '',
  work_permit_no: '',
  blacklisted: false,
  blacklist_note: '',
  weekly_wage: null,
};

/** ฟิลด์ที่ไม่ใช่ข้อความ — ค่าว่าง ('' จาก null เดิม) ห้ามส่งขึ้นเซิร์ฟเวอร์
 *  เพราะสคีมารับเฉพาะชนิดจริง (number/boolean/enum) ไม่ได้รับ ''.
 *  ตัดออกจาก payload = ไม่แตะค่าเดิมในฐานข้อมูล.
 *  หมายเหตุ: ไม่ใส่ `status` (บังคับมีค่าเสมอ) และไม่ใส่ `branch_id`
 *  (สคีมารับ '' โดยตั้งใจ = ปลดสาขาออก) */
const NON_TEXT_FIELDS = [
  'daily_rate',
  'employee_type',
  'pay_only_attended',
  'schedule_mode',
  'work_mode',
  'attendance_exempt',
  'sso_eligible',
  'tax_withhold',
  'has_commission',
  // ทะเบียนชุดใหม่ — ตัวเลข/ตัวเลือก/บูลีน ('' = ไม่ได้กรอก ห้ามส่ง)
  'gender',
  'position_level',
  'blacklisted',
  'weekly_wage',
] as const;

function HREducationSection({ empCode }: { empCode: string }) {
  const query = useEmployeeEducation(empCode);
  const upsert = useUpsertEmployeeEducation(empCode);
  const remove = useDeleteEmployeeEducation(empCode);
  return <EducationSection query={query} upsert={upsert} remove={remove} />;
}

function HRWorkHistorySection({ empCode }: { empCode: string }) {
  const query = useEmployeeWorkHistory(empCode);
  const upsert = useUpsertEmployeeWorkHistory(empCode);
  const remove = useDeleteEmployeeWorkHistory(empCode);
  return <WorkHistorySection query={query} upsert={upsert} remove={remove} />;
}

function HRDocumentsSection({ empCode }: { empCode: string }) {
  const query = useEmployeeDocuments(empCode);
  const create = useCreateEmployeeDocument(empCode);
  const remove = useDeleteEmployeeDocument(empCode);
  return <DocumentsSection query={query} create={create} remove={remove} />;
}

/** ช่องเอกสารประจำตัวพนักงานตามชื่อ (บัตรปชช./ทะเบียนบ้าน/transcript/ใบตรวจร่างกาย/
 *  สัญญาจ้าง/สัญญาแนบท้าย) + เอกสารแนบอื่นๆ. แนบไฟล์ต่อช่อง = เก็บเป็นประเภทนั้น
 *  (แนบซ้ำ = เวอร์ชันใหม่ ของเก่าไม่หาย). */
function EmployeeDocSlots({ empCode }: { empCode: string }) {
  const query = useEmployeeDocuments(empCode);
  const create = useCreateEmployeeDocument(empCode);
  const remove = useDeleteEmployeeDocument(empCode);
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);
  const docs = query.data ?? [];
  const currentByType = new Map<string, (typeof docs)[number]>();
  for (const d of docs) if (d.doc_type && (d.is_current ?? true)) currentByType.set(d.doc_type, d);
  const others = docs.filter((d) => !d.doc_type || d.doc_type === 'other');

  async function upload(slot: EmployeeDocType, file: File) {
    setBusy(slot);
    try {
      const path = await uploadFile(file, 'identity', file.name);
      await create.mutateAsync({
        label: EMPLOYEE_DOC_TYPE_LABELS[slot] ?? file.name,
        r2_path: path,
        mime_type: file.type || undefined,
        size_bytes: file.size,
        ...(slot !== 'other' ? { doc_type: slot } : {}),
      });
      toast.success('อัปโหลดแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'อัปโหลดไม่สำเร็จ');
    } finally {
      setBusy(null);
    }
  }

  async function del(id: string) {
    if (!(await confirm({ title: 'ลบเอกสารนี้?', message: 'ลบแล้วกู้คืนไม่ได้', confirmText: 'ลบ', danger: true }))) return;
    try { await remove.mutateAsync(id); toast.success('ลบแล้ว'); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ'); }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">เอกสารประจำตัวพนักงาน</div>
        <div className="space-y-2">
          {PERSONNEL_DOC_SLOTS.map((slot) => {
            const doc = currentByType.get(slot);
            const url = doc ? resolveFileUrl(doc.url) : null;
            const isRequired = REQUIRED_EMPLOYEE_DOC_TYPES.includes(slot);
            return (
              <div key={slot} className={`flex items-center gap-2 rounded-lg border p-2.5 ${isRequired && !doc ? 'border-rose-200 bg-rose-50/40' : 'border-gray-200'}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium truncate">{EMPLOYEE_DOC_TYPE_LABELS[slot]}</span>
                    {isRequired
                      ? <span className="text-[10px] font-semibold text-rose-600 bg-rose-100 rounded px-1.5 py-0.5 shrink-0">บังคับ</span>
                      : <span className="text-[10px] text-gray-400 shrink-0">ถ้ามี</span>}
                  </div>
                  {doc ? (
                    <div className="text-xs text-emerald-600">
                      ✓ มีแล้ว · {new Date(doc.uploaded_at).toLocaleDateString('th-TH')}
                      {doc.version && doc.version > 1 ? ` · เวอร์ชัน ${doc.version}` : ''}
                    </div>
                  ) : (
                    <div className={`text-xs ${isRequired ? 'text-rose-500' : 'text-gray-400'}`}>ยังไม่มี</div>
                  )}
                </div>
                {url && <a href={url} target="_blank" rel="noreferrer" className="btn-secondary btn-slim text-xs shrink-0">ดู</a>}
                <label className="btn-secondary btn-slim text-xs cursor-pointer shrink-0">
                  {busy === slot ? '...' : (doc ? 'เปลี่ยน' : 'แนบไฟล์')}
                  <input type="file" accept="image/*,.pdf" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(slot, f); e.target.value = ''; }} />
                </label>
              </div>
            );
          })}
        </div>
      </div>

      <div className="pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-gray-500">เอกสารแนบอื่นๆ</div>
          <label className="text-brand-600 text-sm font-medium cursor-pointer inline-flex items-center gap-1">
            {busy === 'other' ? '...' : '+ เพิ่มไฟล์'}
            <input type="file" accept="image/*,.pdf" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload('other', f); e.target.value = ''; }} />
          </label>
        </div>
        {others.length === 0 ? (
          <p className="text-sm text-gray-400 italic">ยังไม่มีเอกสารแนบ</p>
        ) : (
          <div className="space-y-1.5">
            {others.map((d) => {
              const url = resolveFileUrl(d.url);
              return (
                <div key={d.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 min-w-0 truncate">{d.label}</span>
                  {url && <a href={url} target="_blank" rel="noreferrer" className="btn-secondary btn-slim text-xs">ดู</a>}
                  <button type="button" onClick={() => void del(d.id)} className="text-red-500 text-xs">ลบ</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function LineHistorySection({ empCode }: { empCode: string }) {
  const { data, isLoading } = useEmployeeLineHistory(empCode);
  const rows = data?.history ?? [];
  const [viewImg, setViewImg] = useState<{ url: string; title: string } | null>(null);
  const revealIdImage = useRevealNationalIdImage();
  const toast = useToast();
  const confirm = useConfirm();

  async function openIdImage() {
    if (!(await confirm({
      title: 'เปิดดูรูปบัตรประชาชน',
      message: 'การเปิดดูนี้จะถูกบันทึกลงในรายการเปิดดูข้อมูลอ่อนไหว (ระบบจะบันทึกว่าใคร เปิดดูของใคร เมื่อไหร่)',
      confirmText: 'เปิดดู',
    }))) return;
    try {
      const res = await revealIdImage.mutateAsync(empCode);
      const u = resolveFileUrl(res.url);
      if (u) setViewImg({ url: u, title: 'บัตรประชาชน' });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เปิดดูไม่สำเร็จ');
    }
  }

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
        <History className="w-3.5 h-3.5" /> ประวัติการผูก LINE
      </div>
      {isLoading ? (
        <p className="text-xs text-gray-400">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">ยังไม่มีประวัติการผูก LINE</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.id}
              className={`border rounded-lg p-2 ${
                r.is_current ? 'border-green-300 bg-green-50/50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono truncate text-gray-700">{r.line_user_id}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {new Date(r.started_at).toLocaleDateString('th-TH')}
                    {' → '}
                    {r.ended_at ? new Date(r.ended_at).toLocaleDateString('th-TH') : (
                      <span className="text-green-700 font-semibold">ปัจจุบัน</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {r.selfie_url && (
                    <button
                      type="button"
                      onClick={() => { const u = resolveFileUrl(r.selfie_url); if (u) setViewImg({ url: u, title: 'Selfie ยืนยันตัวตน' }); }}
                      title="ดู Selfie"
                      className="p-1 rounded hover:bg-white"
                    >
                      <ImageIcon className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                  {r.national_id_url && (
                    <button
                      type="button"
                      onClick={() => { const u = resolveFileUrl(r.national_id_url); if (u) setViewImg({ url: u, title: 'บัตรประชาชน' }); }}
                      title="ดูบัตรประชาชน"
                      className="p-1 rounded hover:bg-white"
                    >
                      <BadgeCheck className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                  {/* เรื่อง 3b — รูปบัตรถูกล็อก: กดขอดู (ระบบบันทึก log ว่าใครเปิด) */}
                  {r.national_id_image_locked && (
                    <button
                      type="button"
                      onClick={openIdImage}
                      disabled={revealIdImage.isPending}
                      title="ขอดูบัตรประชาชน (บันทึกประวัติการเปิดดู)"
                      className="p-1 rounded hover:bg-white"
                    >
                      <Eye className="w-4 h-4 text-amber-600" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox modal — ดูรูปในแอป ไม่เปิดหน้าใหม่ */}
      {viewImg && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setViewImg(null)}
        >
          <div className="max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between text-white mb-2">
              <span className="text-sm">{viewImg.title}</span>
              <button onClick={() => setViewImg(null)} className="p-1 rounded hover:bg-white/20">
                <X className="w-5 h-5" />
              </button>
            </div>
            <img src={viewImg.url} alt={viewImg.title} className="w-full rounded-lg bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}

// ตั้งสิทธิ์ใช้งานระบบ (role) ให้พนักงานจากหน้ารายชื่อ — เฉพาะ OWNER เห็น/ใช้ได้
type SystemRole = 'EMPLOYEE' | 'SUPERVISOR' | 'MANAGER' | 'HR' | 'OWNER';
const SYSTEM_ROLES: Array<{ value: SystemRole; label: string }> = [
  { value: 'EMPLOYEE', label: 'พนักงาน (ทั่วไป)' },
  { value: 'SUPERVISOR', label: 'หัวหน้า (Supervisor)' },
  { value: 'MANAGER', label: 'ผู้จัดการ (Manager)' },
  { value: 'HR', label: 'HR / ฝ่ายบุคคล' },
  { value: 'OWNER', label: 'เจ้าของระบบ (Owner) — สิทธิ์สูงสุด' },
];
function SystemRoleControl({ empCode, current }: { empCode: string; current: string | null }) {
  const myRole = useAuthStore((s) => s.profile?.role);
  const toast = useToast();
  const setRole = useSetEmployeeRole();
  const [role, setRole2] = useState<SystemRole>((current as SystemRole) ?? 'EMPLOYEE');
  // เจ้าของ + HR ตั้งสิทธิ์ได้ (ฝั่ง backend กัน HR ตั้ง/แก้ "เจ้าของระบบ" อีกชั้น)
  if (myRole !== 'OWNER' && myRole !== 'HR') return null;
  const canAssignOwner = myRole === 'OWNER';
  async function save() {
    // ตั้งเป็นเจ้าของระบบ = ให้สิทธิ์สูงสุด (เห็นเงินเดือน/ตั้งสิทธิ์คนอื่น/ปิดงวด) — ยืนยันก่อน
    if (role === 'OWNER' && current !== 'OWNER') {
      if (!window.confirm('ตั้งคนนี้เป็น "เจ้าของระบบ (Owner)" ?\nจะได้สิทธิ์สูงสุดทั้งหมด (เห็นเงินเดือน ตั้งสิทธิ์คนอื่น ปิดงวดเงินเดือน) — ตั้งเจ้าของได้หลายคน')) return;
    }
    try {
      const r = await setRole.mutateAsync({ emp_code: empCode, role });
      toast.success(r.applied ? 'อัปเดตสิทธิ์แล้ว (มีผลทันที)' : 'บันทึกสิทธิ์แล้ว — จะมีผลเมื่อพนักงานผูก LINE');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ตั้งสิทธิ์ไม่สำเร็จ');
    }
  }
  return (
    <div className="mt-5 pt-4 border-t border-gray-200 space-y-2">
      <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
        <UserCheck className="w-3.5 h-3.5" /> สิทธิ์ใช้งานระบบ
      </div>
      <select className="input" value={role} onChange={(e) => setRole2(e.target.value as SystemRole)}>
        {SYSTEM_ROLES.filter((r) => canAssignOwner || r.value !== 'OWNER').map((r) => (
          <option key={r.value} value={r.value}>{r.label}</option>
        ))}
      </select>
      <button onClick={save} disabled={setRole.isPending} className="btn-secondary text-sm w-full">
        {setRole.isPending ? 'กำลังบันทึก…' : 'บันทึกสิทธิ์'}
      </button>
      <p className="text-[11px] text-gray-400">
        กำหนดได้จากตรงนี้เลย ไม่ต้อง "เชิญผู้ดูแล" ด้วยอีเมล · {canAssignOwner ? 'ตั้ง "เจ้าของระบบ" ได้หลายคน' : 'ตั้ง "เจ้าของระบบ" ได้เฉพาะเจ้าของระบบ'} · ถ้าพนักงานผูก LINE แล้ว สิทธิ์เปลี่ยนทันที ไม่งั้นมีผลตอนผูก LINE
      </p>
    </div>
  );
}

function LinePairingSection({ empCode }: { empCode: string }) {
  const reissue = useReissuePairing();
  const revoke = useRevokeLine();
  const testNotify = useTestNotify();
  const toast = useToast();
  const confirm = useConfirm();
  const tenantCode = useAuthStore((s) => s.profile?.tenant_code ?? '');
  const companyName = useAuthStore((s) => s.profile?.tenant_name ?? '');
  const [issued, setIssued] = useState<{ pairing_code: string; expires_at: string } | null>(null);
  const [copied, setCopied] = useState(false);

  async function onLink() {
    try {
      const r = await reissue.mutateAsync({ emp_code: empCode });
      setIssued({ pairing_code: r.pairing_code, expires_at: r.expires_at });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ออกรหัสไม่สำเร็จ');
    }
  }

  async function onRevoke() {
    const ok = await confirm({
      title: 'ยกเลิกการผูก LINE?',
      message: 'พนักงานจะหลุดจากบัญชี LINE เดิม และต้องผูกใหม่ด้วยรหัสจับคู่ใหม่',
      confirmText: 'ยกเลิกการผูก',
      danger: true,
    });
    if (!ok) return;
    try {
      await revoke.mutateAsync(empCode);
      setIssued(null);
      toast.success('ยกเลิกการผูก LINE แล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ทำรายการไม่สำเร็จ');
    }
  }

  async function onTest() {
    try {
      const r = await testNotify.mutateAsync(empCode);
      if (r.ok) toast.success(r.message ?? 'ส่งทดสอบแล้ว — เช็คใน LINE');
      else toast.error(r.reason ?? 'ส่งทดสอบไม่สำเร็จ');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ส่งทดสอบไม่สำเร็จ');
    }
  }

  async function copyCode() {
    if (!issued) return;
    const inviteLink = LIFF_ID
      ? `https://liff.line.me/${LIFF_ID}/pair?tenant=${encodeURIComponent(tenantCode)}&code=${encodeURIComponent(issued.pairing_code)}`
      : '';
    const expires = new Date(issued.expires_at).toLocaleString('th-TH');
    const text = [
      `📌 เชิญผูกบัญชี LINE เข้าระบบเงินเดือน${companyName ? ` ${companyName}` : ''}`,
      ``,
      `รหัสบริษัท: ${tenantCode}`,
      `รหัสจับคู่: ${issued.pairing_code}`,
      `(หมดอายุ ${expires})`,
      ``,
      ...(inviteLink
        ? [`กดลิงก์นี้เพื่อผูกบัญชีได้เลย (รหัสกรอกให้อัตโนมัติ):`, inviteLink]
        : [`เปิดแอป LINE → เมนูระบบเงินเดือน → "ผูกบัญชี" แล้วกรอกรหัสด้านบน`]),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — code is still visible on screen */
    }
  }

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
        <Link2 className="w-3.5 h-3.5" /> การเชื่อมต่อ LINE
      </div>

      {issued && (
        <div className="bg-white rounded-xl border-2 border-green-400 p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">รหัสบริษัท</div>
              <div className="text-xl font-mono font-bold tracking-widest text-brand-700">{tenantCode || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">รหัสจับคู่</div>
              <div className="text-xl font-mono font-bold tracking-widest text-green-700">{issued.pairing_code}</div>
            </div>
          </div>
          <div className="text-xs text-gray-500">⏰ หมดอายุ {new Date(issued.expires_at).toLocaleString('th-TH')}</div>
          <button onClick={copyCode} className="btn-secondary w-full text-sm">
            {copied ? <><Check className="w-4 h-4" /> คัดลอกแล้ว</> : <><Copy className="w-4 h-4" /> คัดลอกรหัสส่งให้พนักงาน</>}
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <button onClick={onLink} disabled={reissue.isPending} className="btn-secondary text-sm">
          <Link2 className="w-4 h-4" /> {reissue.isPending ? 'กำลังออกรหัส…' : issued ? 'ออกรหัสใหม่' : 'เชื่อม LINE'}
        </button>
        <button onClick={onRevoke} disabled={revoke.isPending} className="btn-secondary text-sm text-red-600">
          <Unlink className="w-4 h-4" /> {revoke.isPending ? 'กำลังยกเลิก…' : 'ยกเลิกการผูก'}
        </button>
      </div>
      <button onClick={onTest} disabled={testNotify.isPending} className="btn-secondary text-sm w-full">
        <Bell className="w-4 h-4" /> {testNotify.isPending ? 'กำลังส่งทดสอบ…' : 'ทดสอบส่งแจ้งเตือนเข้า LINE'}
      </button>
      <p className="text-[11px] text-gray-400">
        “เชื่อม LINE” = ออกรหัสจับคู่ให้พนักงานผูกบัญชีเอง · “ยกเลิกการผูก” ใช้ตอนพนักงานเปลี่ยนเครื่อง/ผูกผิดบัญชี ·
        “ทดสอบส่งแจ้งเตือน” = เช็คว่าพนักงานแอดบอท LINE แล้วรับแจ้งเตือนได้จริง
      </p>
    </div>
  );
}

function DangerZone({ empCode, onDeleted }: { empCode: string; onDeleted: () => void }) {
  const del = useDeleteEmployee();
  const toast = useToast();
  const confirm = useConfirm();

  async function onDelete() {
    const ok = await confirm({
      title: `ลบพนักงาน ${empCode}?`,
      message: 'ใช้เฉพาะกรณีเพิ่มผิด — ลบไม่ได้ถ้ามีประวัติเงินเดือน/เวลาทำงาน (ให้ใช้สถานะลาออก/เลิกจ้างแทน) การลบย้อนกลับไม่ได้',
      confirmText: 'ลบถาวร',
      danger: true,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(empCode);
      toast.success('ลบพนักงานแล้ว');
      onDeleted();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-red-500 flex items-center gap-1">
        <Trash2 className="w-3.5 h-3.5" /> ลบพนักงาน
      </div>
      <button onClick={onDelete} disabled={del.isPending} className="btn-secondary w-full text-sm text-red-600 border-red-200">
        <Trash2 className="w-4 h-4" /> {del.isPending ? 'กำลังลบ…' : 'ลบพนักงานนี้ (กรณีเพิ่มผิด)'}
      </button>
    </div>
  );
}

function PayItemsSection({ empCode }: { empCode: string }) {
  const { data: allowElig } = useAllowanceEligibility(empCode);
  const { data: allowMaster } = useAllowanceMaster();
  const { data: dedElig } = useDeductionEligibility({ emp: empCode });
  const { data: dedMaster } = useDeductionMaster();
  const upsertAllow = useUpsertAllowanceEligibility();
  const deleteAllow = useDeleteAllowanceEligibility();
  const upsertDed = useUpsertDeductionEligibility();
  const deleteDed = useDeleteDeductionEligibility();
  const toast = useToast();
  const confirm = useConfirm();

  const [editingAllow, setEditingAllow] = useState<{ row: AllowanceEligibilityRow | null; isNew: boolean } | null>(null);
  const [editingDed, setEditingDed] = useState<{ row: DeductionEligibilityRow | null; isNew: boolean } | null>(null);

  const aMaster = new Map((allowMaster ?? []).map((m) => [m.id, m]));
  const dMaster = new Map((dedMaster ?? []).map((m) => [m.id, m]));
  const fmt = (n: number) => n.toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const allowances = (allowElig ?? []).filter((e) => e.active);
  const deductions = (dedElig ?? []).filter((e) => e.active);

  async function handleSaveAllow(body: Partial<AllowanceEligibilityRow>) {
    try {
      await upsertAllow.mutateAsync(body);
      setEditingAllow(null);
      toast.success('บันทึกเงินเพิ่มแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }
  async function handleDeleteAllow(id: string, name: string) {
    const ok = await confirm({ title: `ลบ ${name}?`, message: 'รายการจะถูกปิดใช้งาน', danger: true });
    if (!ok) return;
    try {
      await deleteAllow.mutateAsync(id);
      toast.success('ลบแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ');
    }
  }
  async function handleSaveDed(body: Partial<DeductionEligibilityRow>) {
    try {
      await upsertDed.mutateAsync(body);
      setEditingDed(null);
      toast.success('บันทึกเงินหักแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }
  async function handleDeleteDed(id: string, name: string) {
    const ok = await confirm({ title: `ลบ ${name}?`, message: 'รายการจะถูกปิดใช้งาน', danger: true });
    if (!ok) return;
    try {
      await deleteDed.mutateAsync(id);
      toast.success('ลบแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
        <Wallet className="w-3.5 h-3.5" /> เงินเพิ่ม / เงินหักประจำ
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* เงินเพิ่ม */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-medium text-green-700">เงินเพิ่ม</span>
            <button
              type="button"
              onClick={() => setEditingAllow({ row: null, isNew: true })}
              className="text-[11px] text-green-700 hover:underline"
            >
              + เพิ่ม
            </button>
          </div>
          {allowances.length === 0 ? (
            <p className="text-xs text-gray-400">— ไม่มี —</p>
          ) : (
            <div className="space-y-1">
              {allowances.map((e) => {
                const m = aMaster.get(e.allowance_id);
                const amt = e.fixed_amount ?? m?.default_amount ?? null;
                return (
                  <div key={e.id} className="text-xs flex justify-between items-center gap-1 group">
                    <span className="truncate text-gray-700">{m?.name_th ?? 'เงินเพิ่ม'}</span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      <span className="text-green-700">{amt != null ? `+${fmt(amt)}` : '—'}</span>
                      <button
                        type="button"
                        onClick={() => setEditingAllow({ row: e, isNew: false })}
                        aria-label="แก้ไข"
                        className="text-gray-400 hover:text-gray-700"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteAllow(e.id, m?.name_th ?? 'เงินเพิ่ม')}
                        aria-label="ลบ"
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* เงินหัก */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] font-medium text-red-600">เงินหัก</span>
            <button
              type="button"
              onClick={() => setEditingDed({ row: null, isNew: true })}
              className="text-[11px] text-red-600 hover:underline"
            >
              + เพิ่ม
            </button>
          </div>
          {deductions.length === 0 ? (
            <p className="text-xs text-gray-400">— ไม่มี —</p>
          ) : (
            <div className="space-y-1">
              {deductions.map((e) => {
                const m = dMaster.get(e.deduction_id);
                const amt = e.amount ?? m?.default_amount ?? null;
                return (
                  <div key={e.id} className="text-xs flex justify-between items-center gap-1">
                    <span className="truncate text-gray-700">{m?.name_th ?? 'เงินหัก'}</span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      <span className="text-red-600">{amt != null ? `-${fmt(amt)}` : '—'}</span>
                      <button
                        type="button"
                        onClick={() => setEditingDed({ row: e, isNew: false })}
                        aria-label="แก้ไข"
                        className="text-gray-400 hover:text-gray-700"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteDed(e.id, m?.name_th ?? 'เงินหัก')}
                        aria-label="ลบ"
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AllowanceEditModal
        state={editingAllow}
        empCode={empCode}
        masters={allowMaster ?? []}
        onClose={() => setEditingAllow(null)}
        onSave={handleSaveAllow}
        saving={upsertAllow.isPending}
      />
      <DeductionEditModal
        state={editingDed}
        empCode={empCode}
        masters={dedMaster ?? []}
        onClose={() => setEditingDed(null)}
        onSave={handleSaveDed}
        saving={upsertDed.isPending}
      />
    </div>
  );
}

function AllowanceEditModal({
  state,
  empCode,
  masters,
  onClose,
  onSave,
  saving,
}: {
  state: { row: AllowanceEligibilityRow | null; isNew: boolean } | null;
  empCode: string;
  masters: Array<{ id: string; name_th: string; default_amount: number | null; active: boolean }>;
  onClose: () => void;
  onSave: (body: Partial<AllowanceEligibilityRow>) => void;
  saving: boolean;
}) {
  const today = todayISO();
  const [allowanceId, setAllowanceId] = useState('');
  const [amount, setAmount] = useState<string>('');
  const [startDate, setStartDate] = useState(today);

  // Re-seed whenever the row being edited changes (open/edit different).
  useEffect(() => {
    if (state?.row) {
      setAllowanceId(state.row.allowance_id);
      setAmount(state.row.fixed_amount != null ? String(state.row.fixed_amount) : '');
      setStartDate(state.row.start_date);
    } else if (state?.isNew) {
      setAllowanceId('');
      setAmount('');
      setStartDate(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.row?.id, state?.isNew]);

  if (!state) return null;
  const { row, isNew } = state;
  const activeMasters = masters.filter((m) => m.active);
  const title = isNew ? 'เพิ่มเงินเพิ่ม' : 'แก้ไขเงินเพิ่ม';

  function handleSubmit() {
    if (!allowanceId) return;
    const amt = amount.trim() === '' ? null : Number(amount);
    onSave({
      ...(row ? { id: row.id } : {}),
      emp_code: empCode,
      allowance_id: allowanceId,
      fixed_amount: amt,
      start_date: startDate,
      active: true,
    });
  }

  return (
    <Modal open={!!state} onClose={onClose} title={title}>
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-gray-700">รายการ</span>
          <select
            value={allowanceId}
            onChange={(e) => setAllowanceId(e.target.value)}
            disabled={!isNew}
            className="input mt-1 w-full"
          >
            <option value="">— เลือก —</option>
            {activeMasters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name_th}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">จำนวนเงิน (บาท)</span>
          <input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="เว้นว่าง = ใช้ค่าเริ่มต้นของรายการ"
            className="input mt-1 w-full"
            min={0}
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">เริ่มมีผลตั้งแต่</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input mt-1 w-full" />
        </label>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1">
            ยกเลิก
          </button>
          <button onClick={handleSubmit} disabled={saving || !allowanceId} className="btn-primary flex-1">
            {saving ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DeductionEditModal({
  state,
  empCode,
  masters,
  onClose,
  onSave,
  saving,
}: {
  state: { row: DeductionEligibilityRow | null; isNew: boolean } | null;
  empCode: string;
  masters: Array<{ id: string; name_th: string; default_amount: number | null; active: boolean }>;
  onClose: () => void;
  onSave: (body: Partial<DeductionEligibilityRow>) => void;
  saving: boolean;
}) {
  const today = todayISO();
  const [deductionId, setDeductionId] = useState('');
  const [amount, setAmount] = useState<string>('');
  const [startDate, setStartDate] = useState(today);

  useEffect(() => {
    if (state?.row) {
      setDeductionId(state.row.deduction_id);
      setAmount(state.row.amount != null ? String(state.row.amount) : '');
      setStartDate(state.row.start_date);
    } else if (state?.isNew) {
      setDeductionId('');
      setAmount('');
      setStartDate(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.row?.id, state?.isNew]);

  if (!state) return null;
  const { row, isNew } = state;
  const activeMasters = masters.filter((m) => m.active);
  const title = isNew ? 'เพิ่มเงินหัก' : 'แก้ไขเงินหัก';

  function handleSubmit() {
    if (!deductionId) return;
    const amt = amount.trim() === '' ? null : Number(amount);
    onSave({
      ...(row ? { id: row.id } : {}),
      emp_code: empCode,
      deduction_id: deductionId,
      amount: amt,
      start_date: startDate,
      active: true,
    });
  }

  return (
    <Modal open={!!state} onClose={onClose} title={title}>
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-gray-700">รายการ</span>
          <select
            value={deductionId}
            onChange={(e) => setDeductionId(e.target.value)}
            disabled={!isNew}
            className="input mt-1 w-full"
          >
            <option value="">— เลือก —</option>
            {activeMasters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name_th}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">จำนวนเงิน (บาท)</span>
          <input
            type="number"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="เว้นว่าง = ใช้ค่าเริ่มต้นของรายการ"
            className="input mt-1 w-full"
            min={0}
          />
        </label>
        <label className="block text-sm">
          <span className="text-gray-700">เริ่มมีผลตั้งแต่</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input mt-1 w-full" />
        </label>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1">
            ยกเลิก
          </button>
          <button onClick={handleSubmit} disabled={saving || !deductionId} className="btn-primary flex-1">
            {saving ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ShiftInfoSection({ empCode }: { empCode: string }) {
  const { data: assigns } = useShiftAssignments(empCode);
  const { data: shifts } = useShiftMaster();
  const upsert = useUpsertShiftAssignment();
  const del = useDeleteShiftAssignment();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<{ row: ShiftAssignmentRow | null; isNew: boolean } | null>(null);

  const shiftById = new Map((shifts ?? []).map((s) => [s.id, s]));
  const active = (assigns ?? []).filter((a) => a.active);
  const activeShifts = (shifts ?? []).filter((s) => s.active);

  async function handleSave(body: Partial<ShiftAssignmentRow>) {
    try {
      await upsert.mutateAsync(body);
      setEditing(null);
      toast.success('บันทึกกะแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }
  async function handleDelete(id: string, name: string) {
    const ok = await confirm({ title: `ลบกะ ${name}?`, message: 'รายการจะถูกปิดใช้งาน', danger: true });
    if (!ok) return;
    try {
      await del.mutateAsync(id);
      toast.success('ลบแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
          <CalendarClock className="w-3.5 h-3.5" /> กะการทำงาน
        </span>
        <button
          type="button"
          onClick={() => setEditing({ row: null, isNew: true })}
          className="text-[11px] text-brand-700 hover:underline"
        >
          + เพิ่มกะ
        </button>
      </div>
      {active.length === 0 ? (
        <p className="text-xs text-gray-400">ยังไม่ได้กำหนดกะ</p>
      ) : (
        <div className="space-y-2">
          {active.map((a) => {
            const s = shiftById.get(a.shift_id);
            const work = (a.weekdays ?? []).slice().sort((x, y) => x - y);
            const rest = [1, 2, 3, 4, 5, 6, 7].filter((d) => !work.includes(d));
            return (
              <div key={a.id} className="text-xs border border-gray-100 rounded-lg p-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-gray-700 min-w-0">
                    {s?.name ?? a.shift_id} {s && <span className="text-gray-400">· {s.start_time}–{s.end_time}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditing({ row: a, isNew: false })}
                      aria-label="แก้ไข"
                      className="text-gray-400 hover:text-gray-700"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(a.id, s?.name ?? 'กะนี้')}
                      aria-label="ลบ"
                      className="text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {work.map((d) => <span key={d} className="px-1.5 py-0.5 rounded bg-brand-100 text-brand-700">{WD_ABBR[d]}</span>)}
                </div>
                {rest.length > 0 && <div className="text-gray-400 mt-1">หยุด: {rest.map((d) => WD_ABBR[d]).join(' ')}</div>}
                {(a.start_date || a.end_date) && (
                  <div className="text-gray-400 mt-1">
                    {a.start_date ? `เริ่ม ${a.start_date}` : ''}
                    {a.end_date ? ` · ถึง ${a.end_date}` : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ShiftAssignEditModal
        state={editing}
        empCode={empCode}
        masters={activeShifts}
        onClose={() => setEditing(null)}
        onSave={handleSave}
        saving={upsert.isPending}
      />
    </div>
  );
}

function ShiftAssignEditModal({
  state,
  empCode,
  masters,
  onClose,
  onSave,
  saving,
}: {
  state: { row: ShiftAssignmentRow | null; isNew: boolean } | null;
  empCode: string;
  masters: Array<{ id: string; name: string; start_time: string; end_time: string }>;
  onClose: () => void;
  onSave: (body: Partial<ShiftAssignmentRow>) => void;
  saving: boolean;
}) {
  const today = todayISO();
  const [shiftId, setShiftId] = useState('');
  const [weekdays, setWeekdays] = useState<Set<number>>(new Set([1, 2, 3, 4, 5]));
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    if (state?.row) {
      setShiftId(state.row.shift_id);
      setWeekdays(new Set(state.row.weekdays ?? []));
      setStartDate(state.row.start_date);
      setEndDate(state.row.end_date ?? '');
    } else if (state?.isNew) {
      setShiftId('');
      setWeekdays(new Set([1, 2, 3, 4, 5]));
      setStartDate(today);
      setEndDate('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.row?.id, state?.isNew]);

  if (!state) return null;
  const { row, isNew } = state;
  const title = isNew ? 'เพิ่มกะการทำงาน' : 'แก้ไขกะการทำงาน';

  function toggleDay(d: number) {
    const next = new Set(weekdays);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    setWeekdays(next);
  }

  function handleSubmit() {
    if (!shiftId || weekdays.size === 0) return;
    onSave({
      ...(row ? { id: row.id } : {}),
      emp_code: empCode,
      shift_id: shiftId,
      weekdays: Array.from(weekdays).sort((a, b) => a - b),
      start_date: startDate,
      end_date: endDate.trim() === '' ? null : endDate,
      active: true,
    });
  }

  return (
    <Modal open={!!state} onClose={onClose} title={title}>
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-gray-700">รูปแบบกะ</span>
          <select value={shiftId} onChange={(e) => setShiftId(e.target.value)} className="input mt-1 w-full">
            <option value="">— เลือก —</option>
            {masters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.start_time}–{m.end_time})
              </option>
            ))}
          </select>
        </label>

        <div>
          <p className="text-sm text-gray-700 mb-1">วันที่ทำงาน (เลือกได้หลายวัน)</p>
          <div className="flex gap-1 flex-wrap">
            {[1, 2, 3, 4, 5, 6, 7].map((d) => {
              const on = weekdays.has(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  className={`px-2.5 py-1 rounded-full text-xs ${
                    on ? 'bg-brand-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
                  }`}
                >
                  {WD_ABBR[d]}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            หยุด: {[1, 2, 3, 4, 5, 6, 7].filter((d) => !weekdays.has(d)).map((d) => WD_ABBR[d]).join(' ') || '—'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            <span className="text-gray-700">เริ่ม</span>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input mt-1 w-full" />
          </label>
          <label className="block text-sm">
            <span className="text-gray-700">สิ้นสุด</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              placeholder="(ไม่ระบุ = ใช้ต่อเนื่อง)"
              className="input mt-1 w-full"
            />
          </label>
        </div>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="btn-secondary flex-1">
            ยกเลิก
          </button>
          <button onClick={handleSubmit} disabled={saving || !shiftId || weekdays.size === 0} className="btn-primary flex-1">
            {saving ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** สิทธิ์วันลาปีนี้ของพนักงานคนนี้ — อ่านจาก useQuotas() (ปีปัจจุบัน) กรองด้วย emp_code.
 *  HR/Owner แก้/ปรับลดสิทธิ์ ป่วย/กิจ/พักร้อน ได้ตรงนี้ (บันทึกผ่าน POST /hr/quotas).
 *  "ยกมา" อ่านอย่างเดียว (มาจากการยกยอดสิ้นปี). ตั้งค่าขั้นสูง (วันที่ใช้ไปก่อนเริ่มระบบ)
 *  ยังอยู่ที่เมนู "ตั้งสิทธิ์การลา (โควต้า)". */
function LeaveQuotaCard({ empCode }: { empCode: string }) {
  const { data: quotas = [], isLoading } = useQuotas();
  const upsert = useUpsertQuota();
  const toast = useToast();
  const year = new Date().getFullYear();
  const row = quotas.find((q) => q.emp_code === empCode);

  const [sick, setSick] = useState('30');
  const [personal, setPersonal] = useState('3');
  const [vacation, setVacation] = useState('6');

  // sync ค่าจาก DB เมื่อโหลดเสร็จ / สลับพนักงาน
  useEffect(() => {
    if (row) {
      setSick(String(row.sick_quota));
      setPersonal(String(row.personal_quota));
      setVacation(String(row.vacation_quota));
    } else {
      setSick('30'); setPersonal('3'); setVacation('6');
    }
  }, [row]);

  const numOr0 = (v: string) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const carried = row?.carried_days ?? 0;

  async function save() {
    try {
      await upsert.mutateAsync({
        emp_code: empCode,
        year,
        sick_quota: numOr0(sick),
        personal_quota: numOr0(personal),
        vacation_quota: numOr0(vacation),
      });
      toast.success('บันทึกสิทธิ์วันลาแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  if (isLoading) return <p className="text-sm text-gray-400">กำลังโหลด…</p>;

  return (
    <div className="space-y-4 max-w-lg">
      <div className="text-xs uppercase tracking-wider text-gray-500">สิทธิ์วันลาปีนี้ ({year})</div>
      {!row && (
        <p className="text-sm text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          ยังไม่ได้ตั้งสิทธิ์วันลาปีนี้ — กรอกจำนวนวันแล้วกดบันทึกเพื่อสร้าง
        </p>
      )}
      <div className="grid grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs text-gray-500">ลาป่วย (วัน)</span>
          <input type="number" step="0.5" min="0" value={sick}
            onChange={(e) => setSick(e.target.value)} className="input mt-1" />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">ลากิจ (วัน)</span>
          <input type="number" step="0.5" min="0" value={personal}
            onChange={(e) => setPersonal(e.target.value)} className="input mt-1" />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">พักร้อน (วัน)</span>
          <input type="number" step="0.5" min="0" value={vacation}
            onChange={(e) => setVacation(e.target.value)} className="input mt-1" />
        </label>
      </div>
      <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-sm">
        <span className="text-gray-500">ยกมาจากปีก่อน (พักร้อน): </span>
        <span className="font-semibold text-gray-700">{carried} วัน</span>
        <span className="text-xs text-gray-400"> — ปรับจากการยกยอดสิ้นปี ไม่แก้ตรงนี้</span>
      </div>
      <button type="button" onClick={save} disabled={upsert.isPending} className="btn-primary">
        {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึกสิทธิ์วันลา'}
      </button>
      <p className="text-xs text-gray-400">
        ตั้งค่าขั้นสูง (วันที่ใช้ไปก่อนเริ่มระบบ) ที่เมนู “ตั้งสิทธิ์การลา (โควต้า)”
      </p>
    </div>
  );
}

function EmployeeModal({
  initial,
  onClose,
}: {
  initial: EmployeeRow | null;
  onClose: () => void;
}) {
  const isEdit = !!initial;
  const [tab, setTab] = useState<
    'personal' | 'contact' | 'job' | 'finance' | 'shift' | 'leave' | 'education' | 'docs' | 'system'
  >('personal');
  // เว้นพื้นที่ด้านซ้ายเท่าความกว้าง sidebar (desktop) เพื่อให้เมนูซ้ายยังเห็นอยู่ตอนแก้ไข.
  // อ่านสถานะ "ย่อ sidebar" จาก localStorage (เดียวกับ AdminLayout): ย่อ=w-16 / ปกติ=w-64.
  const sidebarRailed = (() => {
    try { return localStorage.getItem('payroll-edge.sidebar.rail') === '1'; } catch { return false; }
  })();
  const upsert = useUpsertEmployee();
  const rotationPatterns = useRotationPatterns();
  const toast = useToast();
  const nudge = useNudgeProfile();
  const profilePct = Math.round(initial?.profile_completion_pct ?? 0);
  async function sendProfileNudge() {
    if (!initial?.emp_code) return;
    try {
      const r = await nudge.mutateAsync(initial.emp_code);
      if (r.sent) toast.success('ส่งแจ้งเตือนให้กรอกข้อมูลทาง LINE แล้ว');
      else toast.info('พนักงานยังไม่ได้ผูก LINE — แจ้งเตือนไม่ได้');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'แจ้งเตือนไม่สำเร็จ');
    }
  }
  // เรื่อง 3b — เลขบัตรถูกมาส์กสำหรับ HR; กด "ขอดู" เพื่อเปิดค่าจริง (ระบบบันทึก log)
  const revealId = useRevealNationalId();
  const confirm = useConfirm();
  const [idUnlocked, setIdUnlocked] = useState(false);
  const idLocked = !!initial?.national_id_masked && !idUnlocked;
  async function revealNationalId() {
    if (!initial?.emp_code) return;
    if (!(await confirm({
      title: 'เปิดดูเลขบัตรประชาชน',
      message: 'การเปิดดูนี้จะถูกบันทึกลงในรายการเปิดดูข้อมูลอ่อนไหว (ระบบจะบันทึกว่าใคร เปิดดูของใคร เมื่อไหร่)',
      confirmText: 'เปิดดู',
    }))) return;
    try {
      const res = await revealId.mutateAsync(initial.emp_code);
      setValue('national_id', res.national_id);
      setIdUnlocked(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เปิดดูไม่สำเร็จ');
    }
  }
  // SoT (ADR 0003): pick department/position from the org structure managed at
  // /owner/org (PayPilot owns it). Values are stored as the name text (employees
  // table keeps department/position TEXT; backend ensureDepartment still runs).
  const { data: orgDepts = [] } = useOrgDepartments();
  const { data: orgPositions = [] } = useOrgPositions();
  const { data: branches = [] } = useBranches();
  const { data: supervisorCandidates = [] } = useSupervisorCandidates();
  const deptNames = orgDepts.map((d) => d.name);
  const { register, handleSubmit, control, setValue, watch, formState: { errors } } = useForm<FormData>({
    defaultValues: initial
      ? {
          ...EMPTY,
          ...Object.fromEntries(
            Object.entries(initial).map(([k, v]) => [k, v ?? '']),
          ),
        } as FormData
      : EMPTY,
  });

  // ตำแหน่งกรองตามแผนกที่เลือก (cascade): เลือกแผนกก่อน → เห็นเฉพาะตำแหน่งของแผนกนั้น
  // ถ้ายังไม่เลือกแผนก หรือแผนกที่เลือกไม่อยู่ในโครงสร้าง → แสดงตำแหน่งทั้งหมด
  const selectedDeptId = orgDepts.find((d) => d.name === watch('department'))?.id ?? null;
  const positionTitles = (selectedDeptId
    ? orgPositions.filter((p) => p.department_id === selectedDeptId)
    : orgPositions
  ).map((p) => p.title);

  async function onSubmit(data: FormData) {
    try {
      const payload = { ...data };
      // ⭐ ฟิลด์ที่ไม่ใช่ข้อความ (ตัวเลข/บูลีน/ตัวเลือก): defaultValues แปลง null → ''
      // ให้ช่องกรอกทำงานได้ แต่ค่าว่างพวกนี้ส่งขึ้นเซิร์ฟเวอร์ไม่ได้ (สคีมารับเฉพาะชนิดจริง)
      // → เคยทำให้พนักงานรายเดือน (daily_rate = NULL) กดบันทึกแล้วเด้ง
      //   "daily_rate: Expected number, received string" ทุกคน
      // ไม่ส่งคีย์ = ไม่แตะค่าเดิม (upsert เขียนเฉพาะคอลัมน์ที่ส่งมา)
      for (const k of NON_TEXT_FIELDS) {
        const v = (payload as Record<string, unknown>)[k];
        if (v === '' || v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v))) {
          delete (payload as Record<string, unknown>)[k];
        }
      }
      // เลขบัตร: รับเว้นวรรค/ขีดกลางที่ช่องกรอก แต่ตัดเหลือตัวเลขล้วนตอนยืนยัน
      // (เว้นค่ามาส์กที่มี • ไว้ — เซิร์ฟเวอร์จะไม่เขียนทับค่าจริง)
      if (typeof payload.national_id === 'string' && !payload.national_id.includes('•')) {
        payload.national_id = payload.national_id.replace(/\D/g, '');
      }
      // ไม่มีช่องบังคับ — ถ้าไม่กรอกรหัสพนักงาน (เพิ่มใหม่) สร้างให้อัตโนมัติ
      // เพื่อให้ยังมีรหัสประจำตัวสำหรับอ้างอิง/ผูก LINE/คิดเงินภายหลัง
      if (!payload.emp_code?.trim()) {
        payload.emp_code = `EMP${Date.now().toString().slice(-8)}`;
      }
      await upsert.mutateAsync(payload as Record<string, unknown>);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <div className={`fixed inset-y-0 right-0 left-0 z-40 bg-white flex flex-col border-l border-gray-200 ${sidebarRailed ? 'lg:left-16' : 'lg:left-64'}`}>
      {/* หัวเต็มพื้นที่ (เว้นเมนูซ้ายไว้บนจอใหญ่) */}
      <div className="shrink-0 flex items-center gap-3 border-b border-gray-200 px-4 py-3">
        <button onClick={onClose} className="shrink-0"><X className="w-5 h-5" /></button>
        <h2 className="font-semibold text-lg">
          {isEdit ? `แก้ไข ${initial?.emp_code}` : 'เพิ่มพนักงาน'}
        </h2>
        {isEdit && (
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <div className="text-right leading-none">
              <div className="text-[10px] text-gray-400">กรอกข้อมูลแล้ว</div>
              <div className={`text-sm font-semibold ${profilePct >= 100 ? 'text-emerald-600' : profilePct >= 60 ? 'text-amber-500' : 'text-rose-500'}`}>
                {profilePct}%
              </div>
            </div>
            {profilePct < 100 && (
              <button type="button" onClick={() => void sendProfileNudge()} disabled={nudge.isPending}
                className="btn-secondary btn-slim text-xs inline-flex items-center gap-1 shrink-0">
                <Bell className="w-3.5 h-3.5" /> {nudge.isPending ? 'กำลังส่ง…' : 'แจ้งเตือนให้กรอก'}
              </button>
            )}
          </div>
        )}
      </div>
      {/* แท็บ — เอกสารต้องมีรหัสพนักงานก่อน จึงโชว์เฉพาะตอนแก้ไข */}
      {isEdit && (
        <div className="shrink-0 flex overflow-x-auto whitespace-nowrap border-b border-gray-200 px-2">
          <button type="button" onClick={() => setTab('personal')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'personal' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            ส่วนตัว
          </button>
          <button type="button" onClick={() => setTab('contact')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'contact' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            การติดต่อ
          </button>
          <button type="button" onClick={() => setTab('job')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'job' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            งาน
          </button>
          <button type="button" onClick={() => setTab('finance')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'finance' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            การเงิน
          </button>
          <button type="button" onClick={() => setTab('shift')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'shift' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            วันหยุด/ตารางเวร
          </button>
          <button type="button" onClick={() => setTab('leave')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'leave' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            วันลาคงเหลือ
          </button>
          <button type="button" onClick={() => setTab('education')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'education' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            การศึกษา
          </button>
          <button type="button" onClick={() => setTab('docs')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'docs' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            เอกสาร
          </button>
          <button type="button" onClick={() => setTab('system')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === 'system' ? 'border-brand-500 text-brand-600' : 'border-transparent text-gray-500'}`}>
            ระบบ
          </button>
        </div>
      )}
      {/* เนื้อหา */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto w-full p-4">
        <form id="emp-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {tab === 'personal' && (
          <>
          {/* Identity */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">ข้อมูลพื้นฐาน</div>
            <div>
              <label className="label">รหัสพนักงาน</label>
              <input
                {...register('emp_code', { maxLength: 20 })}
                className="input"
                readOnly={isEdit}
                placeholder={isEdit ? undefined : 'เว้นว่างได้ — ระบบจะสร้างให้อัตโนมัติ'}
              />
              {errors.emp_code && <p className="text-xs text-red-500 mt-1">รหัสพนักงานไม่เกิน 20 ตัวอักษร</p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">ชื่อ</label>
                <input {...register('first_name')} className="input" />
              </div>
              <div>
                <label className="label">นามสกุล</label>
                <input {...register('last_name')} className="input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">คำนำหน้าชื่อ</label>
                <select {...register('title_th')} className="input">
                  <option value="">— ไม่ระบุ —</option>
                  <option value="นาย">นาย</option>
                  <option value="นาง">นาง</option>
                  <option value="นางสาว">นางสาว</option>
                </select>
              </div>
              <div>
                <label className="label">ชื่อเล่น</label>
                <input {...register('nickname')} className="input" />
              </div>
            </div>
          </div>

          {/* ชื่อภาษาอังกฤษ — ใช้ในรายงานทะเบียนพนักงาน/เอกสารต่างประเทศ */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">ชื่อภาษาอังกฤษ (ถ้ามี)</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">คำนำหน้า (EN)</label>
                <select {...register('title_en')} className="input">
                  <option value="">— ไม่ระบุ —</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Ms.">Ms.</option>
                </select>
              </div>
              <div>
                <label className="label">ชื่อเล่น (EN)</label>
                <input {...register('nickname_en', { maxLength: 50 })} className="input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">ชื่อ (EN)</label>
                <input {...register('first_name_en', { maxLength: 100 })} className="input" />
              </div>
              <div>
                <label className="label">นามสกุล (EN)</label>
                <input {...register('last_name_en', { maxLength: 100 })} className="input" />
              </div>
            </div>
          </div>

          {/* Profile (5 fields that drive completion %) */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">ประวัติส่วนตัว</div>
            <div>
              <label className="label">เลขประจำตัวประชาชน (13 หลัก)</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={20}
                  readOnly={idLocked}
                  {...register('national_id', {
                    // รับเว้นวรรค/ขีดกลางได้ — ตรวจ 13 หลักหลังตัดอักขระ · ปล่อยผ่านค่ามาส์ก (มี •)
                    validate: (v) =>
                      !v || (v as string).includes('•') || /^\d{13}$/.test((v as string).replace(/\D/g, '')) || 'ต้อง 13 หลัก',
                  })}
                  className={`input flex-1 ${idLocked ? 'bg-gray-50 text-gray-500' : ''}`}
                />
                {idLocked && (
                  <button
                    type="button"
                    onClick={revealNationalId}
                    disabled={revealId.isPending}
                    className="btn-secondary shrink-0 px-3"
                    title="ขอดูเลขบัตรจริง (บันทึกประวัติการเปิดดู)"
                  >
                    <Eye className="w-4 h-4" /> ขอดู
                  </button>
                )}
              </div>
              {idLocked && (
                <p className="text-xs text-gray-400 mt-1">ซ่อนไว้เพื่อความปลอดภัย — กด “ขอดู” เพื่อเปิดค่าจริง (ระบบบันทึกว่าใครเปิดดู)</p>
              )}
              {errors.national_id && (
                <p className="text-xs text-red-500 mt-1">{errors.national_id.message}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">วันเกิด</label>
                <input type="date" {...register('birth_date')} className="input" />
              </div>
              <div>
                <label className="label">เลขประกันสังคม</label>
                <input {...register('sso_number')} className="input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">เพศ</label>
                <select {...register('gender')} className="input">
                  <option value="">— ไม่ระบุ —</option>
                  <option value="male">ชาย</option>
                  <option value="female">หญิง</option>
                  <option value="other">อื่น ๆ</option>
                </select>
              </div>
              <div>
                <label className="label">สัญชาติ</label>
                <input {...register('nationality', { maxLength: 50 })} className="input" placeholder="ไทย" />
              </div>
            </div>
          </div>

          {/* เอกสารพนักงานต่างด้าว — คนไทยปล่อยว่างทั้งบล็อก */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">เอกสารต่างด้าว (ถ้าไม่ใช่คนไทย)</div>
            <div>
              <label className="label">เลขประจำตัวคนซึ่งไม่มีสัญชาติไทย</label>
              <input {...register('alien_id', { maxLength: 20 })} className="input" inputMode="numeric" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">เลขหนังสือเดินทาง</label>
                <input {...register('passport_no', { maxLength: 30 })} className="input" />
              </div>
              <div>
                <label className="label">เลขใบอนุญาตทำงาน</label>
                <input {...register('work_permit_no', { maxLength: 30 })} className="input" />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">ที่อยู่</div>
            <div>
              <label className="label">ที่อยู่ปัจจุบัน</label>
              <textarea
                rows={2}
                placeholder="บ้านเลขที่ ถนน ตำบล อำเภอ จังหวัด รหัสไปรษณีย์"
                className="input resize-none"
                {...register('address_current', { maxLength: 500 })}
              />
            </div>
            <div>
              <label className="label">ที่อยู่ตามทะเบียนบ้าน</label>
              <textarea
                rows={2}
                placeholder="ปล่อยว่างถ้าเหมือนที่อยู่ปัจจุบัน"
                className="input resize-none"
                {...register('address_registered', { maxLength: 500 })}
              />
            </div>
          </div>

          {/* Emergency contact */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">ผู้ติดต่อฉุกเฉิน</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">ชื่อ-นามสกุล</label>
                <input className="input" {...register('emergency_name', { maxLength: 100 })} />
              </div>
              <div>
                <label className="label">ความสัมพันธ์</label>
                <select className="input" {...register('emergency_relationship')}>
                  <option value="">— เลือก —</option>
                  <option value="แม่">แม่</option>
                  <option value="พ่อ">พ่อ</option>
                  <option value="คู่สมรส">คู่สมรส</option>
                  <option value="พี่/น้อง">พี่/น้อง</option>
                  <option value="ลูก">ลูก</option>
                  <option value="ญาติ">ญาติ</option>
                  <option value="เพื่อน">เพื่อน</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label">เบอร์โทรฉุกเฉิน</label>
              <input
                type="tel"
                inputMode="tel"
                className="input"
                {...register('emergency_phone', { maxLength: 20 })}
              />
            </div>
          </div>

          {/* Documents — read-only HR view of paths (uploads happen on self-service /profile) */}
          {(initial?.id_copy_path || initial?.bankbook_path) && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-gray-500">เอกสารแนบ</div>
              <div className="text-xs text-gray-500">
                {initial?.id_copy_path && <div>• สำเนาบัตร ปชช.: <code className="text-gray-700">{initial.id_copy_path}</code></div>}
                {initial?.bankbook_path && <div>• หน้าสมุดบัญชี: <code className="text-gray-700">{initial.bankbook_path}</code></div>}
                <div className="italic mt-1">พนักงานอัปโหลดเองที่หน้า "ประวัติของฉัน"</div>
              </div>
            </div>
          )}
          </>
          )}

          {tab === 'contact' && (
          <>
          {/* Contact */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">การติดต่อ</div>
            <div>
              <label className="label">อีเมล</label>
              <input type="email" {...register('email')} className="input" />
            </div>
            <div>
              <label className="label">เบอร์โทร</label>
              <input type="tel" {...register('phone')} className="input" />
            </div>
            <div>
              <label className="label">หัวหน้างาน (ผู้อนุมัติ)</label>
              <select {...register('supervisor_emp_code')} className="input">
                <option value="">— ไม่มีหัวหน้า —</option>
                {(() => {
                  const byDept = new Map<string, typeof supervisorCandidates>();
                  for (const c of supervisorCandidates) {
                    const k = c.department || 'ไม่ระบุแผนก';
                    if (!byDept.has(k)) byDept.set(k, []);
                    byDept.get(k)!.push(c);
                  }
                  return [...byDept.entries()].map(([dept, list]) => (
                    <optgroup key={dept} label={dept}>
                      {list.map((c) => (
                        <option key={c.emp_code} value={c.emp_code}>
                          {c.name}{c.position ? ` · ${c.position}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  ));
                })()}
              </select>
              <p className="text-xs text-gray-400 mt-1">เลือกชื่อหัวหน้า (เฉพาะตำแหน่งหัวหน้า/ผู้จัดการ) → หัวหน้าคนนั้นจะเห็นพนักงานในเมนู “ทีมของฉัน” และเป็นผู้อนุมัติลา/OT ของพนักงานคนนี้</p>
            </div>
          </div>
          </>
          )}

          {tab === 'job' && (
          <>
          {/* Work */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">งาน</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">แผนก</label>
                <Controller
                  name="department"
                  control={control}
                  render={({ field }) => (
                    <OrgNameSelect
                      value={field.value ?? ''}
                      onChange={(v) => { field.onChange(v); setValue('position', ''); }}
                      options={deptNames}
                      placeholder="— เลือกแผนก —"
                      emptyHint="ยังไม่มีแผนก — เพิ่มที่เมนูโครงสร้างองค์กร"
                    />
                  )}
                />
              </div>
              <div>
                <label className="label">ตำแหน่ง</label>
                <Controller
                  name="position"
                  control={control}
                  render={({ field }) => (
                    <OrgNameSelect
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      options={positionTitles}
                      placeholder="— เลือกตำแหน่ง —"
                      emptyHint="ยังไม่มีตำแหน่ง — เพิ่มที่เมนูโครงสร้างองค์กร"
                    />
                  )}
                />
              </div>
            </div>
            <div>
              <label className="label">สาขาหลัก</label>
              <select {...register('branch_id')} className="input">
                <option value="">— ไม่ระบุสาขา —</option>
                {branches.filter((b) => b.is_active).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}{b.code ? ` (${b.code})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">วันเริ่มงาน</label>
                <input type="date" {...register('start_date')} className="input" />
              </div>
              <div>
                <label className="label">วันสิ้นสุด (ถ้ามี)</label>
                <input type="date" {...register('end_date')} className="input" />
              </div>
            </div>
            <div>
              <label className="label">สถานะ</label>
              <select {...register('status')} className="input">
                <option value="active">ปกติ</option>
                <option value="probation">ทดลองงาน</option>
                <option value="resigned">ลาออก</option>
                <option value="terminated">เลิกจ้าง</option>
              </select>
            </div>
            <div>
              <label className="label">รูปแบบทำงาน</label>
              <select {...register('work_mode')} className="input">
                <option value="office">ออฟฟิศ — ต้องเช็คอินในพื้นที่ออฟฟิศ</option>
                <option value="wfh">ทำงานที่บ้าน — ข้ามการตรวจพื้นที่</option>
                <option value="hybrid">ผสม — เลือกได้ทุกวันว่าอยู่ออฟฟิศหรือบ้าน</option>
              </select>
            </div>
          </div>

          {/* สังกัดชั้นอื่น + ข้อมูลทะเบียน (ใช้ในรายงานทะเบียนพนักงาน 42 คอลัมน์) */}
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-gray-500">สังกัด / ทะเบียน</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">ฝ่ายงาน</label>
                <input {...register('division', { maxLength: 100 })} className="input" placeholder="ชั้นเหนือแผนก" />
              </div>
              <div>
                <label className="label">หน่วยงาน</label>
                <input {...register('section', { maxLength: 100 })} className="input" placeholder="ชั้นใต้แผนก" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">กลุ่มพนักงาน</label>
                <input {...register('employee_group', { maxLength: 100 })} className="input" />
              </div>
              <div>
                <label className="label">ระดับตำแหน่ง</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={99}
                  {...register('position_level', { setValueAs: (v) => (v === '' || v == null ? '' : Number(v)) })}
                  className="input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">รหัสลายนิ้วมือ (เครื่องสแกน)</label>
                <input {...register('fingerprint_id', { maxLength: 50 })} className="input" />
              </div>
              <div>
                <label className="label">เงินประจำสัปดาห์</label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  {...register('weekly_wage', { setValueAs: (v) => (v === '' || v == null ? '' : Number(v)) })}
                  className="input"
                />
                <p className="text-xs text-gray-400 mt-1">เก็บไว้ในทะเบียน/รายงานเท่านั้น — ระบบเงินเดือนไม่ได้ใช้คำนวณ</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">วันที่บรรจุ</label>
                <input type="date" {...register('appointed_date')} className="input" />
                <p className="text-xs text-gray-400 mt-1">วันที่ผ่านทดลองงาน (ต่างจากวันเริ่มงาน)</p>
              </div>
              <div>
                <label className="label">วันที่หมดสัญญาจ้าง</label>
                <input type="date" {...register('contract_end_date')} className="input" />
                <p className="text-xs text-gray-400 mt-1">เฉพาะสัญญามีกำหนดระยะเวลา</p>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...register('blacklisted')} className="w-4 h-4" />
                แบล็กลิสต์ (ไม่รับกลับเข้าทำงาน)
              </label>
              <input
                {...register('blacklist_note', { maxLength: 500 })}
                className="input mt-2"
                placeholder="เหตุผล (ถ้ามี)"
              />
            </div>
          </div>
          </>
          )}

          {tab === 'finance' && (
          <>
          {isEdit && initial && (
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wider text-gray-500">เงินเดือน / ธนาคาร</div>
              <SalarySection empCode={initial.emp_code} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label">ธนาคาร</label>
                  <select {...register('bank')} className="input">
                    <option value="">— เลือก —</option>
                    <option value="SCB">ไทยพาณิชย์</option>
                    <option value="KBANK">กสิกร</option>
                    <option value="BBL">กรุงเทพ</option>
                    <option value="KTB">กรุงไทย</option>
                    <option value="TTB">ทหารไทยธนชาต</option>
                    <option value="BAY">กรุงศรี</option>
                    <option value="GSB">ออมสิน</option>
                    <option value="BAAC">ธ.ก.ส.</option>
                    <option value="OTHER">อื่นๆ</option>
                  </select>
                </div>
                <div>
                  <label className="label">เลขที่บัญชี (เต็ม)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={30}
                    placeholder={initial?.bank_account_masked ? `ปัจจุบัน ••${initial.bank_account_masked} (เว้นว่าง=ไม่เปลี่ยน)` : 'กรอกเลขบัญชีเต็ม'}
                    {...register('bank_account_full', {
                      pattern: { value: /^[0-9-]*$/, message: 'ตัวเลขเท่านั้น' },
                    })}
                    className="input"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">เก็บแบบเข้ารหัส · ใช้ทำไฟล์โอนธนาคาร · แสดงเฉพาะ 4 หลักท้าย</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2 pt-4 border-t border-gray-200">
            <div className="text-xs uppercase tracking-wider text-gray-500">ภาษี / ประกันสังคม</div>
            <label className="flex items-start gap-2 text-sm bg-brand-50 rounded p-2">
              <input type="checkbox" {...register('sso_eligible')} className="mt-0.5 shrink-0" />
              <span>
                เข้าประกันสังคม (หัก 5% ตอนคิดเงินเดือน)
                <span className="block text-xs text-gray-500">ติ๊ก = ระบบหัก ปกส. 5% (สูงสุด 875 บ./เดือน) อัตโนมัติทุกงวด · เอาออก = คนนี้ไม่หัก ปกส.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm bg-brand-50 rounded p-2">
              <input type="checkbox" {...register('tax_withhold')} className="mt-0.5 shrink-0" />
              <span>
                หักภาษี ณ ที่จ่าย
                <span className="block text-xs text-gray-500">ติ๊ก = ระบบหักภาษี ณ ที่จ่ายตามเกณฑ์อัตโนมัติทุกงวด · เอาออก = คนนี้ไม่หักภาษี</span>
              </span>
            </label>
          </div>

          <div className="space-y-2 pt-4 border-t border-gray-200">
            <div className="text-xs uppercase tracking-wider text-gray-500">ค่าคอม / การจ่าย</div>
            <label className="flex items-start gap-2 text-sm bg-pink-50 rounded p-2">
              <input type="checkbox" {...register('has_commission')} className="mt-0.5 shrink-0" />
              <span>
                มีค่าคอมมิชชั่น — หักรายการต่างๆ ตอนจ่ายค่าคอม
                <span className="block text-xs text-gray-500">ติ๊ก = งวดเงินเดือนจ่ายแค่ฐาน + ภาษี/ปกส./กองทุน · รายการหัก (สาย/ลา/ขาด) + OT + เบี้ย + เงินเพิ่ม/หัก ย้ายไปหักตอนจ่ายค่าคอมมิชชั่น (ดูสรุปที่หน้าเงินเดือน) · เอาออก = หักจากเงินเดือนตามปกติ</span>
              </span>
            </label>
            {isEdit && (
              <label className="flex items-start gap-2 text-sm bg-amber-50 rounded p-2">
                <input type="checkbox" {...register('attendance_exempt')} className="mt-0.5 shrink-0" />
                <span>
                  ไม่ต้องสแกนเข้างาน / ไม่นับเวลา — จ่ายเงินเดือนเต็ม
                  <span className="block text-xs text-gray-500">เหมาะกับผู้บริหาร/พนักงานเหมาเดือนที่ไม่ต้องตอกบัตร · คนนี้จะไม่เห็นหน้าสแกน และคิดเงินเต็มฐานไม่หักลา/ขาด/ไม่คิด OT</span>
                </span>
              </label>
            )}
          </div>
          </>
          )}
        </form>

        {tab === 'finance' && isEdit && initial && (
          <div className="pt-4 border-t border-gray-200 space-y-2">
            <div className="text-xs uppercase tracking-wider text-gray-500">เงินเพิ่ม / เงินหัก</div>
            <PayItemsSection empCode={initial.emp_code} />
          </div>
        )}

        {tab === 'shift' && isEdit && initial && (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1">
                <CalendarClock className="w-3.5 h-3.5" /> วันหยุด / ตารางเวร
              </div>
              <div>
                <label className="label">วิธีคิดวันหยุด</label>
                <select {...register('schedule_mode')} className="input" form="emp-form">
                  <option value="company_weekend">ใช้วันหยุดบริษัท (เสาร์/อาทิตย์ตามตั้งค่า)</option>
                  <option value="roster">จัดตารางเวรเอง (วันหยุดหมุนเวียน)</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">เลือก “จัดตารางเวรเอง” ถ้าคนนี้หยุดสลับวัน — ระบบจะคิดเงินตามตารางเวร ไม่ใช่วันหยุดบริษัท</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label">รอบหมุนเวร (อัตโนมัติ)</label>
                  <select {...register('rotation_pattern_id')} className="input" form="emp-form">
                    <option value="">— ไม่ใช้รอบหมุน —</option>
                    {(rotationPatterns.data ?? []).filter((p) => p.active).map((p) => (
                      <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">วันเริ่มรอบหมุน</label>
                  <input type="date" {...register('rotation_anchor_date')} className="input" form="emp-form" />
                </div>
              </div>
              <p className="text-xs text-gray-400">วันที่ตรงกับช่องแรกของรอบ — ตั้งรอบหมุน + กด “สร้างตารางเดือนนี้” ที่แท็บหมุนเวร · แก้แล้วกด “บันทึก” ที่แท็บอื่น (เช่น ส่วนตัว/งาน) เพื่อยืนยัน</p>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <ShiftInfoSection empCode={initial.emp_code} />
            </div>
          </div>
        )}

        {tab === 'leave' && isEdit && initial && (
          <LeaveQuotaCard empCode={initial.emp_code} />
        )}

        {tab === 'education' && isEdit && initial && (
          <div className="space-y-4">
            <HREducationSection empCode={initial.emp_code} />
            <div className="pt-4 border-t border-gray-200">
              <HRWorkHistorySection empCode={initial.emp_code} />
            </div>
          </div>
        )}

        {tab === 'docs' && isEdit && initial && (
          <EmployeeDocSlots empCode={initial.emp_code} />
        )}

        {tab === 'system' && isEdit && initial && (
          <div className="space-y-4">
            <LinePairingSection empCode={initial.emp_code} />
            <SystemRoleControl empCode={initial.emp_code} current={initial.assigned_role ?? null} />
            <div className="pt-4 border-t border-gray-200">
              <LineHistorySection empCode={initial.emp_code} />
            </div>
            <div className="pt-4 border-t border-red-100">
              <DangerZone empCode={initial.emp_code} onDeleted={onClose} />
            </div>
          </div>
        )}
        </div>
      </div>
      {(tab === 'personal' || tab === 'contact' || tab === 'job' || tab === 'finance' || tab === 'shift') && (
        <div className="shrink-0 border-t border-gray-200 p-3">
          <button type="submit" form="emp-form" disabled={upsert.isPending} className="btn-primary w-full">
            {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      )}
    </div>
  );
}

/** เงินเดือนพื้นฐาน — เจ้าของ + HR ที่ได้สิทธิ์ "ตั้ง/แก้ฐานเงินเดือน" (edit_salary)
 *  ตรงกับ backend ที่ gate ด้วย requireCapability('edit_salary'). */
function SalarySection({ empCode }: { empCode: string }) {
  const role = useAuthStore((s) => s.profile?.role);
  const { data: caps } = useMyCapabilities();
  const canEditSalary = role === 'OWNER' || (caps?.includes('edit_salary') ?? false);
  const setSalary = useSetEmployeeSalary();
  const delSalary = useDeleteEmployeeSalary();
  const salaryQ = useEmployeeSalary(canEditSalary ? empCode : null);
  const toast = useToast();
  const confirm = useConfirm();
  const [amount, setAmount] = useState('');
  const [effDate, setEffDate] = useState(() => todayISO());
  if (!canEditSalary) return null;
  const history = salaryQ.data?.history ?? [];

  async function save() {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) { toast.error('กรอกเงินเดือนให้ถูกต้อง'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effDate)) { toast.error('เลือกวันที่มีผล'); return; }
    try {
      await setSalary.mutateAsync({ emp_code: empCode, base_salary: n, effective_date: effDate });
      toast.success('บันทึกเงินเดือนแล้ว');
      setAmount('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  async function remove(effective_date: string, amountLabel: string) {
    const ok = await confirm({
      title: 'ลบรายการเงินเดือนนี้?',
      message: `${amountLabel} (มีผล ${new Date(effective_date).toLocaleDateString('th-TH')}) — ลบแล้วกู้คืนไม่ได้`,
      confirmText: 'ลบ',
      danger: true,
    });
    if (!ok) return;
    try {
      await delSalary.mutateAsync({ emp_code: empCode, effective_date });
      toast.success('ลบรายการแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ');
    }
  }

  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">ประวัติเงินเดือน (เฉพาะเจ้าของ)</div>
      <div className="mb-4">
        {salaryQ.isLoading ? (
          <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-400">กำลังโหลด…</div>
        ) : !history.length ? (
          <div className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-400">ยังไม่ได้ตั้งเงินเดือน</div>
        ) : (
          <div className="space-y-1.5">
            {history.map((h, i) => {
              const isCurrent = i === 0 && h.effective_to == null;
              const from = new Date(h.effective_from).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
              const to = h.effective_to ? new Date(h.effective_to).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
              return (
                <div key={h.effective_from} className={`rounded-lg px-3 py-2 flex items-center gap-2 ${isCurrent ? 'bg-brand-50 border border-brand-200' : 'bg-gray-50'}`}>
                  <div className="text-xs flex-1 min-w-0">
                    {isCurrent && <span className="mr-1.5 rounded bg-brand-600 px-1.5 py-0.5 text-[10px] font-medium text-white">ปัจจุบัน</span>}
                    <span className="text-gray-500">{to ? `${from} – ${to}` : `ตั้งแต่ ${from}`}</span>
                  </div>
                  <span className={`tabular-nums ${isCurrent ? 'font-semibold text-brand-700' : 'text-gray-600'}`}>฿{h.base_salary.toLocaleString('en-US')}</span>
                  <button
                    type="button"
                    onClick={() => remove(h.effective_from, `฿${h.base_salary.toLocaleString('en-US')}`)}
                    disabled={delSalary.isPending}
                    className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    aria-label="ลบรายการเงินเดือน"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="text-xs text-gray-500 mb-1.5">ตั้ง / แก้เงินเดือน</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label">เงินเดือนพื้นฐาน (บาท)</label>
          <input type="number" step="0.01" className="input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="เช่น 15000" />
        </div>
        <div>
          <label className="label">มีผลตั้งแต่</label>
          <input type="date" className="input" value={effDate} onChange={(e) => setEffDate(e.target.value)} />
        </div>
      </div>
      <button onClick={save} disabled={setSalary.isPending} className="btn-secondary w-full mt-2">
        {setSalary.isPending ? 'กำลังบันทึก…' : 'บันทึกเงินเดือน'}
      </button>
      <p className="text-[11px] text-gray-400 mt-1">เก็บแบบเข้ารหัส เห็นเฉพาะเจ้าของ · ตั้งได้หลายช่วงตามวันที่มีผล (เก็บประวัติเงินเดือน)</p>
    </div>
  );
}

function CompletionBadge({ pct }: { pct: number }) {
  const color =
    pct === 100 ? 'bg-green-100 text-green-700'
    : pct >= 60 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return <span className={`badge ${color}`}>{pct}%</span>;
}

/** Content-only version used by the People hub. */
/** เพิ่มพนักงานแบบ minimal (ชื่อ+เลขบัตร+แผนก/ตำแหน่ง) — ฟอร์มเดียวกับแท็บ
 *  "เพิ่มพนักงาน" เพื่อให้ทุกทางเพิ่มพนักงานตรงกัน. คลิกแถว = แก้ไขฟอร์มเต็ม. */
function AddEmployeeModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center">
      <div className="bg-white w-full max-w-2xl rounded-t-2xl lg:rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-semibold text-lg">เพิ่มพนักงาน</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <OwnerOnboardContent />
      </div>
    </div>
  );
}

export function HREmployeesContent() {
  const { data, isLoading } = useEmployees(true, true) as { data: EmployeeRow[] | undefined; isLoading: boolean };
  const role = useAuthStore((s) => s.profile?.role);
  const { data: caps } = useMyCapabilities();
  // "ตั้งฐานเงินเดือนหลายคน" ผูกกับสิทธิ์ตั้งเงินเดือน ไม่ใช่ตำแหน่งเจ้าของ
  const canSetSalary = role === 'OWNER' || (caps?.includes('edit_salary') ?? false);
  // ส่งออกทะเบียนพนักงาน = สิทธิ์เดียวกับส่งออกภาษี/รายงาน (route ฝั่ง server ก็ตรวจซ้ำ)
  const canExportRegistry = role === 'OWNER' || (caps?.includes('export_tax') ?? false);
  const isHqActor = role === 'OWNER' || role === 'HR';
  const toast = useToast();
  const [editing, setEditing] = useState<EmployeeRow | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showBulkSalary, setShowBulkSalary] = useState(false);
  const [showBulkApprover, setShowBulkApprover] = useState(false);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const { data: orgDepts = [] } = useOrgDepartments();
  const { data: orgPositions = [] } = useOrgPositions();
  // 🚨 hook ทุกตัวต้องอยู่เหนือ early return ด้านล่าง (`if (isLoading) return …`)
  // ถ้าประกาศใต้บรรทัดนั้น: render แรกตอนกำลังโหลดจะเรียก hook น้อยกว่าตอนโหลดเสร็จ
  // ⇒ React error #310 "rendered more hooks than during the previous render"
  // = หน้ารายชื่อพนักงานกลายเป็นจอว่างเปล่าทั้งหน้า ไม่มีข้อความอะไรเลย
  const [exportingRegistry, setExportingRegistry] = useState(false);

  if (isLoading) {
    return <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>;
  }

  const employees = data ?? [];
  const q = search.trim().toLowerCase();
  const filtered = employees.filter((e) => {
    if (deptFilter && (e.department ?? '') !== deptFilter) return false;
    if (positionFilter && (e.position ?? '') !== positionFilter) return false;
    if (!q) return true;
    return (
      e.emp_code.toLowerCase().includes(q) ||
      (e.first_name ?? '').toLowerCase().includes(q) ||
      (e.last_name ?? '').toLowerCase().includes(q) ||
      (e.nickname ?? '').toLowerCase().includes(q) ||
      (e.department ?? '').toLowerCase().includes(q)
    );
  });

  function openAdd() {
    setEditing(null);
    setShowModal(true);
  }
  function openEdit(emp: EmployeeRow) {
    setEditing(emp);
    setShowModal(true);
  }
  function closeModal() {
    setShowModal(false);
    setEditing(null);
  }

  // รายงานทะเบียนพนักงาน (ข้อมูลพื้นฐาน) 42 คอลัมน์ — ตามฟอร์แมตที่ลูกค้าใช้อยู่.
  // กรองตามแผนก/ตำแหน่งบนหน้าจอไม่มีผลกับไฟล์ (รายงานคือทะเบียนทั้งบริษัท) —
  // ตัวกรองที่ไฟล์รองรับคือสาขา/สถานะ ซึ่งส่งผ่าน query string
  // (state ของปุ่มนี้ประกาศไว้บนสุดของ component — ห้ามย้ายลงมาใต้ `if (isLoading) return`)
  async function handleExportRegistry() {
    setExportingRegistry(true);
    try {
      await apiDownload('/hr/employees/registry-export', 'ทะเบียนพนักงาน.csv');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดทะเบียนพนักงานไม่สำเร็จ');
    } finally {
      setExportingRegistry(false);
    }
  }

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-sm text-gray-600">
          {employees.length} คน · กรอกประวัติครบ {employees.filter((e) => (e.profile_completion_pct ?? 0) === 100).length} คน
        </p>
        <div className="flex items-center gap-2 shrink-0">
          {canExportRegistry && (
            <button onClick={handleExportRegistry} disabled={exportingRegistry} className="btn-secondary text-sm">
              <Download className="w-4 h-4" /> {exportingRegistry ? 'กำลังสร้างไฟล์…' : 'ทะเบียนพนักงาน (CSV)'}
            </button>
          )}
          {isHqActor && (
            <button onClick={() => setShowBulkApprover(true)} className="btn-secondary text-sm">
              <UserCheck className="w-4 h-4" /> ตั้งผู้อนุมัติหลายคน
            </button>
          )}
          {canSetSalary && (
            <button onClick={() => setShowBulkSalary(true)} className="btn-secondary text-sm">
              <Coins className="w-4 h-4" /> ตั้งฐานเงินเดือนหลายคน
            </button>
          )}
          {/* เพิ่มพนักงาน = งานสำนักงานใหญ่ (ตั้งค่าจ้าง/เลขบัญชี) — API กั้นแล้ว
              ถ้ายังโชว์ปุ่ม ผู้จัดการจะกรอกฟอร์มจนจบแล้วเจอ 403 ตอนกดบันทึก */}
          {isHqActor && (
            <button onClick={openAdd} className="btn-primary text-sm">
              <UserPlus className="w-4 h-4" /> เพิ่มพนักงาน
            </button>
          )}
        </div>
      </div>
      {/* Search */}
      <div className="mb-3 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="ค้นหาด้วยรหัส ชื่อ หรือแผนก…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-9"
        />
      </div>

      {/* Filter แผนก/ตำแหน่ง */}
      {(orgDepts.length > 0 || orgPositions.length > 0) && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <select
            className="input"
            value={deptFilter}
            onChange={(e) => { setDeptFilter(e.target.value); setPositionFilter(''); }}
          >
            <option value="">ทุกแผนก</option>
            {orgDepts.map((d) => (
              <option key={d.id} value={d.name}>{d.name}</option>
            ))}
          </select>
          <select
            className="input"
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
          >
            <option value="">ทุกตำแหน่ง</option>
            {orgPositions
              .filter((p) => {
                if (!deptFilter) return true;
                const did = orgDepts.find((d) => d.name === deptFilter)?.id ?? null;
                return p.department_id === did;
              })
              .map((p) => (
                <option key={p.id} value={p.title}>{p.title}</option>
              ))}
          </select>
        </div>
      )}

      {/* List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {filtered.map((emp) => (
          <button
            key={emp.emp_code}
            type="button"
            onClick={() => openEdit(emp)}
            className="card p-3 text-left hover:shadow-md transition group"
          >
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">
                  {emp.first_name} {emp.last_name}
                  {emp.nickname && <span className="text-gray-500 text-sm font-normal"> ({emp.nickname})</span>}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {emp.emp_code} · {emp.department} · {emp.position}
                </div>
              </div>
              <Pencil className="w-4 h-4 text-gray-300 group-hover:text-brand-600 shrink-0 mt-1" />
            </div>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <span className={STATUS_BADGE[emp.status]}>
                {STATUS_LABEL[emp.status]}
              </span>
              <CompletionBadge pct={emp.profile_completion_pct ?? 0} />
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-gray-500 py-8 col-span-full">
            {employees.length === 0 ? 'ยังไม่มีพนักงาน' : 'ไม่พบพนักงานตามคำค้น'}
          </p>
        )}
      </div>

      {showModal && (editing
        ? <EmployeeModal initial={editing} onClose={closeModal} />
        : <AddEmployeeModal onClose={closeModal} />
      )}
      {showBulkSalary && <BulkSalaryModal onClose={() => setShowBulkSalary(false)} />}
      {showBulkApprover && <BulkApproverModal employees={employees} onClose={() => setShowBulkApprover(false)} />}
    </>
  );
}

/**
 * ตั้ง "หัวหน้างาน (ผู้อนุมัติลา/OT)" ให้พนักงานหลายคนพร้อมกัน (HR/Owner).
 * - เลือกผู้อนุมัติ 1 คนจากรายชื่อหัวหน้า/ผู้จัดการ (หรือ "ล้างผู้อนุมัติ")
 * - ติ๊กพนักงานหลายคนจาก list (ค้นหา + เลือกทั้งหมดได้) แล้วบันทึกทีเดียว
 * - โชว์ผู้อนุมัติปัจจุบันของแต่ละคน เพื่อให้เห็นว่าใครยังไม่ได้ตั้ง
 * หัวหน้าที่ตั้งจะเป็นคนรับแจ้งเตือน + อนุมัติคำขอของพนักงานกลุ่มนี้.
 */
function BulkApproverModal({ employees, onClose }: { employees: EmployeeRow[]; onClose: () => void }) {
  const { data: candidates = [] } = useSupervisorCandidates();
  const bulkSet = useBulkSetSupervisor();
  const toast = useToast();
  const [supervisor, setSupervisor] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  // เฉพาะคน active/probation — คนลาออก/เลิกจ้างไม่ต้องตั้งผู้อนุมัติ
  const list = employees.filter((e) => e.status === 'active' || e.status === 'probation');
  const nameByCode = new Map(employees.map((e) => [
    e.emp_code,
    (`${e.first_name} ${e.last_name}`.trim() || e.emp_code) + (e.nickname ? ` (${e.nickname})` : ''),
  ]));
  const candByCode = new Map(candidates.map((c) => [c.emp_code, c]));
  const supName = (code: string | null | undefined) =>
    !code ? null : candByCode.get(code)?.name ?? nameByCode.get(code) ?? code;

  const q = search.trim().toLowerCase();
  const filtered = q
    ? list.filter((e) =>
        e.emp_code.toLowerCase().includes(q) ||
        `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
        (e.nickname ?? '').toLowerCase().includes(q) ||
        (e.department ?? '').toLowerCase().includes(q))
    : list;

  function toggle(code: string) {
    setSelected((p) => { const n = new Set(p); n.has(code) ? n.delete(code) : n.add(code); return n; });
  }
  const allSel = filtered.length > 0 && filtered.every((e) => selected.has(e.emp_code));
  function toggleAll() {
    setSelected((p) => {
      const n = new Set(p);
      if (allSel) filtered.forEach((e) => n.delete(e.emp_code));
      else filtered.forEach((e) => n.add(e.emp_code));
      return n;
    });
  }

  const clearing = supervisor === '__clear__';
  async function save() {
    if (selected.size === 0) { toast.error('เลือกพนักงานก่อน'); return; }
    if (!supervisor) { toast.error('เลือกผู้อนุมัติ (หรือ “ล้างผู้อนุมัติ”)'); return; }
    const sup = clearing ? null : supervisor;
    // คัดคนที่เลือกเป็นหัวหน้าตัวเองออก (worker กันไว้อีกชั้น)
    const codes = [...selected].filter((c) => c !== sup);
    if (codes.length === 0) { toast.error('พนักงานเป็นหัวหน้าของตัวเองไม่ได้'); return; }
    setSaving(true);
    try {
      const r = await bulkSet.mutateAsync({ supervisor_emp_code: sup, emp_codes: codes });
      toast.success(clearing ? `ล้างผู้อนุมัติแล้ว ${r.set} คน` : `ตั้ง ${supName(sup)} เป็นผู้อนุมัติให้ ${r.set} คน`);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="ตั้งผู้อนุมัติหลายคน" size="max-w-2xl">
      {candidates.length === 0 ? (
        <div className="py-8 text-center">
          <AlertTriangle className="mx-auto mb-2 h-8 w-8 text-amber-500" />
          <p className="text-gray-700">ยังไม่มีหัวหน้า/ผู้จัดการในระบบ</p>
          <p className="mt-1 text-sm text-gray-500">ตั้งตำแหน่งให้เป็น “หัวหน้า/ผู้จัดการ” หรือกำหนด role ก่อน แล้วค่อยมาเลือกเป็นผู้อนุมัติ</p>
          <button onClick={onClose} className="btn-secondary mt-4 text-sm">ปิด</button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* เลือกผู้อนุมัติ */}
          <div>
            <label className="label">ผู้อนุมัติ (หัวหน้างาน)</label>
            <select value={supervisor} onChange={(e) => setSupervisor(e.target.value)} className="input">
              <option value="">— เลือกผู้อนุมัติ —</option>
              {(() => {
                const byDept = new Map<string, typeof candidates>();
                for (const c of candidates) {
                  const d = c.department || 'ไม่ระบุแผนก';
                  if (!byDept.has(d)) byDept.set(d, []);
                  byDept.get(d)!.push(c);
                }
                return [...byDept.entries()].map(([dept, cs]) => (
                  <optgroup key={dept} label={dept}>
                    {cs.map((c) => (
                      <option key={c.emp_code} value={c.emp_code}>
                        {c.name}{c.position ? ` · ${c.position}` : ''}
                      </option>
                    ))}
                  </optgroup>
                ));
              })()}
              <option value="__clear__">— ล้างผู้อนุมัติ (ไม่มีหัวหน้า) —</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">
              {clearing
                ? 'พนักงานที่เลือกจะไม่มีหัวหน้า — คำขอลา/OT จะถูกส่งไปที่ HR (ถ้าไม่มี HR ส่งให้เจ้าของ)'
                : 'หัวหน้าที่เลือกจะเป็นคนรับแจ้งเตือน + อนุมัติคำขอลา/OT ของพนักงานที่เลือก'}
            </p>
          </div>

          {/* ค้นหา + เลือกทั้งหมด */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input type="text" placeholder="ค้นหาพนักงาน…" value={search} onChange={(e) => setSearch(e.target.value)} className="input pl-9 py-1.5 text-sm" />
            </div>
            <button onClick={toggleAll} className="shrink-0 text-xs text-teal-700 hover:underline">
              {allSel ? 'ล้างที่เลือก' : 'เลือกทั้งหมด'}
            </button>
          </div>

          {/* รายชื่อพนักงาน */}
          <div className="max-h-[46vh] space-y-1.5 overflow-y-auto pr-1">
            {filtered.map((e) => {
              const isSel = selected.has(e.emp_code);
              const curSup = supName(e.supervisor_emp_code);
              return (
                <label key={e.emp_code} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 ${isSel ? 'border-teal-300 bg-teal-50/50' : 'border-gray-200'}`}>
                  <input type="checkbox" checked={isSel} onChange={() => toggle(e.emp_code)} className="shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {e.first_name} {e.last_name}
                      {e.nickname && <span className="font-normal text-gray-500"> ({e.nickname})</span>}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {e.emp_code}{e.position ? ` · ${e.position}` : ''}{e.department ? ` · ${e.department}` : ''}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs">
                    {curSup
                      ? <span className="text-gray-500">ผู้อนุมัติ: {curSup}</span>
                      : <span className="text-amber-600">ยังไม่ตั้ง</span>}
                  </span>
                </label>
              );
            })}
            {filtered.length === 0 && <p className="py-6 text-center text-sm text-gray-500">ไม่พบพนักงานตามคำค้น</p>}
          </div>

          {/* บันทึก */}
          <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
            <span className="text-sm text-gray-600">เลือกแล้ว <b>{selected.size}</b> คน</span>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-secondary text-sm">ยกเลิก</button>
              <button onClick={save} disabled={saving || selected.size === 0 || !supervisor} className="btn-primary text-sm">
                {saving ? 'กำลังบันทึก…' : clearing ? 'ล้างผู้อนุมัติ' : 'ตั้งเป็นผู้อนุมัติ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * ตั้งฐานเงินเดือนหลายคนพร้อมกัน (OWNER) — โชว์เฉพาะคน active ที่ยังไม่มีฐานเงินเดือน.
 * - เลือกหลายคน (multi-select) แล้วใส่เลขเดียวพร้อมกัน (คนฐานเท่ากัน)
 * - หรือ "ดึงจากโครงสร้าง" — เลือกตำแหน่ง/แผนก×ระดับ แล้วเติมค่า ต่ำสุด/กลาง/สูงสุด
 * - วิเคราะห์ compa-ratio (เงินเดือน ÷ ค่ากลางของกรอบ) บอกว่าอยู่ ต่ำ/ใน/เหนือ กรอบ
 * ส่งเป็นชุดละ ≤ 150 คน กัน subrequest limit ฝั่ง worker.
 */
type ZoneInfo = { ratio: number; zone: 'below' | 'within' | 'above' };
function compaInfo(amount: number, band: { min: number; mid: number; max: number } | null): ZoneInfo | null {
  if (!band || !band.mid || !amount) return null;
  const zone = amount < band.min ? 'below' : amount > band.max ? 'above' : 'within';
  return { ratio: amount / band.mid, zone };
}
const ZONE: Record<ZoneInfo['zone'], { label: string; cls: string }> = {
  below: { label: 'ต่ำกว่ากรอบ', cls: 'text-amber-600' },
  within: { label: 'ในกรอบ', cls: 'text-emerald-600' },
  above: { label: 'เหนือกรอบ', cls: 'text-rose-600' },
};

function BulkSalaryModal({ onClose }: { onClose: () => void }) {
  const prep = useBulkSalaryPrep(true);
  const bulkSet = useBulkSetSalary();
  const toast = useToast();
  const [effDate, setEffDate] = useState(() => todayISO());
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkVal, setBulkVal] = useState('');
  const [refId, setRefId] = useState('');
  const [refField, setRefField] = useState<'min' | 'mid' | 'max'>('mid');
  const [saving, setSaving] = useState(false);

  const list: BulkSalaryEmp[] = prep.data?.employees ?? [];
  const refs: SalaryReference[] = [
    ...(prep.data?.references.positions ?? []),
    ...(prep.data?.references.structure ?? []),
  ];
  const hasRefs = refs.length > 0;

  function setAmt(code: string, v: string) {
    setAmounts((p) => ({ ...p, [code]: v.replace(/[^\d]/g, '') }));
  }
  function setMany(codes: string[], v: string) {
    const clean = v.replace(/[^\d]/g, '');
    setAmounts((p) => { const n = { ...p }; for (const c of codes) n[c] = clean; return n; });
  }
  function toggle(code: string) {
    setSelected((p) => { const n = new Set(p); n.has(code) ? n.delete(code) : n.add(code); return n; });
  }
  const allSelected = list.length > 0 && selected.size === list.length;
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(list.map((e) => e.emp_code)));
  }
  function fillSuggested() {
    setAmounts((p) => {
      const next = { ...p };
      for (const e of list) if (e.suggested != null && !next[e.emp_code]) next[e.emp_code] = String(e.suggested);
      return next;
    });
  }
  function applyNumberToSelected() {
    if (selected.size === 0) { toast.error('เลือกพนักงานก่อน'); return; }
    if (!bulkVal) { toast.error('กรอกตัวเลข'); return; }
    setMany([...selected], bulkVal);
  }
  function applyRefToSelected() {
    if (selected.size === 0) { toast.error('เลือกพนักงานก่อน'); return; }
    const r = refs.find((x) => `${x.kind}:${x.id}` === refId);
    if (!r) { toast.error('เลือกตำแหน่ง/ระดับจากโครงสร้าง'); return; }
    setMany([...selected], String(Math.round(r[refField])));
  }

  const suggestedCount = list.filter((e) => e.suggested != null).length;
  const readyItems = list
    .map((e) => ({ emp_code: e.emp_code, base_salary: Number(amounts[e.emp_code]) }))
    .filter((x) => Number.isFinite(x.base_salary) && x.base_salary > 0);

  // สรุป compa-ratio
  const zoneCount = { below: 0, within: 0, above: 0 };
  for (const e of list) {
    const amt = Number(amounts[e.emp_code]);
    const info = compaInfo(amt, e.band);
    if (info) zoneCount[info.zone]++;
  }

  async function save() {
    if (readyItems.length === 0) { toast.error('ยังไม่ได้กรอกเงินเดือน'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effDate)) { toast.error('เลือกวันที่มีผล'); return; }
    setSaving(true);
    try {
      let set = 0;
      const failed: Array<{ emp_code: string; reason: string }> = [];
      for (let i = 0; i < readyItems.length; i += 150) {
        const chunk = readyItems.slice(i, i + 150);
        const r = await bulkSet.mutateAsync({ effective_date: effDate, items: chunk });
        set += r.set;
        failed.push(...r.failed);
      }
      if (failed.length === 0) { toast.success(`ตั้งฐานเงินเดือนแล้ว ${set} คน`); onClose(); }
      else toast.error(`สำเร็จ ${set} คน · ไม่สำเร็จ ${failed.length} คน`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="ตั้งฐานเงินเดือนหลายคน" size="max-w-3xl">
      {prep.isLoading ? (
        <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
      ) : list.length === 0 ? (
        <div className="py-8 text-center">
          <Check className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
          <p className="text-gray-700">พนักงาน active ทุกคนมีฐานเงินเดือนแล้ว</p>
          <button onClick={onClose} className="btn-secondary mt-4 text-sm">ปิด</button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <AlertTriangle className="mr-1 inline h-4 w-4 align-text-bottom" />
            มี <b>{list.length}</b> คนยังไม่มีฐานเงินเดือน (จาก active {prep.data?.total_active} คน)
          </div>

          {/* วันที่มีผล + ใช้ค่าแนะนำ */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">วันที่มีผล</span>
              <input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} className="input !w-auto py-1.5 text-sm" />
            </label>
            {suggestedCount > 0 && (
              <button onClick={fillSuggested} className="btn-secondary text-sm">
                <Sparkles className="h-4 w-4" /> ใช้ค่าแนะนำ ({suggestedCount})
              </button>
            )}
          </div>

          {/* แถบใส่ค่าให้หลายคนพร้อมกัน */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">
                เลือกแล้ว {selected.size} คน
              </p>
              <button onClick={toggleAll} className="text-xs text-teal-700 hover:underline">
                {allSelected ? 'ล้างที่เลือก' : 'เลือกทั้งหมด'}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">฿</span>
                <input inputMode="numeric" value={bulkVal} onChange={(e) => setBulkVal(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="ใส่เลขเดียว" className="input !w-32 py-1.5 pl-5 text-right text-sm tabular-nums" />
              </div>
              <button onClick={applyNumberToSelected} disabled={selected.size === 0} className="btn-secondary text-sm">
                ใส่ให้ที่เลือก
              </button>
              {hasRefs && (
                <>
                  <span className="text-xs text-gray-400">หรือดึงจากโครงสร้าง</span>
                  <select value={refId} onChange={(e) => setRefId(e.target.value)} className="input !w-auto py-1.5 text-sm">
                    <option value="">— ตำแหน่ง / แผนก×ระดับ —</option>
                    {(prep.data?.references.positions.length ?? 0) > 0 && (
                      <optgroup label="ตามตำแหน่ง (กรอบเงินเดือน)">
                        {prep.data!.references.positions.map((r) => (
                          <option key={`position:${r.id}`} value={`position:${r.id}`}>
                            {r.title}{r.level != null ? ` (L${r.level})` : ''}{r.department ? ` · ${r.department}` : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {(prep.data?.references.structure.length ?? 0) > 0 && (
                      <optgroup label="ตามแผนก × ระดับ (โครงสร้าง)">
                        {prep.data!.references.structure.map((r) => (
                          <option key={`structure:${r.id}`} value={`structure:${r.id}`}>{r.title}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <select value={refField} onChange={(e) => setRefField(e.target.value as 'min' | 'mid' | 'max')} className="input !w-auto py-1.5 text-sm">
                    <option value="min">ต่ำสุด</option>
                    <option value="mid">ค่ากลาง</option>
                    <option value="max">สูงสุด</option>
                  </select>
                  <button onClick={applyRefToSelected} disabled={selected.size === 0} className="btn-secondary text-sm">ใส่ค่า</button>
                </>
              )}
            </div>
          </div>

          {/* รายชื่อ */}
          <div className="max-h-[46vh] space-y-1.5 overflow-y-auto pr-1">
            {list.map((e) => {
              const amt = Number(amounts[e.emp_code]);
              const info = compaInfo(amt, e.band);
              const isSel = selected.has(e.emp_code);
              return (
                <div key={e.emp_code} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${isSel ? 'border-teal-300 bg-teal-50/50' : 'border-gray-200'}`}>
                  <input type="checkbox" checked={isSel} onChange={() => toggle(e.emp_code)} className="shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{e.name}</p>
                    <p className="truncate text-xs text-gray-500">
                      {e.emp_code}{e.position ? ` · ${e.position}` : ''}{e.department ? ` · ${e.department}` : ''}
                      {e.band && <span className="text-gray-400"> · กรอบ ฿{e.band.min.toLocaleString('en-US')}–{e.band.max.toLocaleString('en-US')}</span>}
                      {e.match_quality === 'fuzzy' && e.matched_title && (
                        <span className="text-amber-600" title="จับคู่ตำแหน่งโดยประมาณ — ตรวจสอบให้ชัวร์ก่อนบันทึก"> · ≈ เทียบกับ “{e.matched_title}”</span>
                      )}
                    </p>
                  </div>
                  {info ? (
                    <span className={`shrink-0 text-xs font-medium tabular-nums ${ZONE[info.zone].cls}`} title="compa-ratio (เงินเดือน ÷ ค่ากลาง)">
                      {Math.round(info.ratio * 100)}% · {ZONE[info.zone].label}
                    </span>
                  ) : e.suggested != null ? (
                    <button type="button" onClick={() => setAmt(e.emp_code, String(e.suggested))}
                      className="shrink-0 rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-700 hover:bg-teal-100">
                      แนะนำ ฿{e.suggested.toLocaleString('en-US')}
                    </button>
                  ) : null}
                  <div className="relative shrink-0">
                    <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">฿</span>
                    <input inputMode="numeric" value={amounts[e.emp_code] ?? ''} onChange={(ev) => setAmt(e.emp_code, ev.target.value)}
                      placeholder="ฐานเงินเดือน" className="input !w-28 py-1.5 pl-5 text-right text-sm tabular-nums" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* สรุป + บันทึก */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
            <div className="text-sm text-gray-600">
              กรอกแล้ว <b>{readyItems.length}</b>/{list.length} คน
              {(zoneCount.below + zoneCount.within + zoneCount.above) > 0 && (
                <span className="ml-2 text-xs">
                  · <span className="text-emerald-600">ในกรอบ {zoneCount.within}</span>
                  {zoneCount.below > 0 && <> · <span className="text-amber-600">ต่ำกว่า {zoneCount.below}</span></>}
                  {zoneCount.above > 0 && <> · <span className="text-rose-600">เหนือ {zoneCount.above}</span></>}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-secondary text-sm">ยกเลิก</button>
              <button onClick={() => void save()} disabled={saving || readyItems.length === 0} className="btn-primary text-sm">
                {saving ? 'กำลังบันทึก…' : `บันทึก ${readyItems.length || ''} คน`}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Standalone page kept for legacy /hr/employees (now redirects via People hub). */
/**
 * Pick a department/position by NAME from the org structure (SoT: /owner/org).
 * - has options → dropdown; a current value not in the list is preserved as a
 *   "(เดิม)" option so legacy/free-typed data never silently blanks on save.
 * - no options yet → free text input + hint (don't block adding an employee
 *   before the org is set up).
 */
function OrgNameSelect({
  value, onChange, options, placeholder, emptyHint,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  emptyHint: string;
}) {
  if (options.length === 0) {
    return (
      <>
        <input className="input" value={value} onChange={(e) => onChange(e.target.value)} />
        <p className="text-[11px] text-gray-400 mt-1">{emptyHint}</p>
      </>
    );
  }
  const inList = !value || options.includes(value);
  return (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {!inList && <option value={value}>{value} (เดิม)</option>}
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export function HREmployees() {
  return (
    <AdminLayout title="พนักงานทั้งหมด">
      <HREmployeesContent />
    </AdminLayout>
  );
}
